/* eslint-disable */
// ─────────────────────────────────────────────────────────────────────────────
// GENERATED — do not edit by hand.
//
// Extracted verbatim from legacy/CapaciTEA_v20.html by scripts/extract-engine.mjs.
// Every function below is a byte-for-byte copy of the legacy implementation; the only
// addition is the createEngine() wrapper, which supplies the `S` these functions read.
// To change a formula, change it here AND in the legacy file, or retire the legacy file.
//
// 66 declarations, closed transitively over 46 public roots.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Bind the calculation engine to one hydrated state object.
 * @param {object} S — the full `S`-shaped state (see packages/engine/src/types.d.ts)
 */
export function createEngine(S) {
  const r1 = n => Math.round(n*10)/10;

  const dName  = id => S.depts.find(d=>d.id===id)?.name || id;

  const getRole = id => S.roles.find(r=>r.id===id);

  // All skills defined across all roles in TEA
  function allOrgSkills(){
    const set = new Set();
    S.roles.forEach(r=>(r.skills||[]).forEach(s=>set.add(s.trim())));
    return [...set].sort();
  }

  // Skills available within a specific department (or all depts if 'ALL')
  function deptSkills(deptId){
    const roles = deptId==='ALL' ? S.roles : S.roles.filter(r=>r.dept===deptId);
    const set = new Set();
    roles.forEach(r=>(r.skills||[]).forEach(s=>set.add(s.trim())));
    return [...set].sort();
  }

  // For a given skill, check coverage status within a dept
  // Returns: { status: 'covered'|'stretched'|'gap'|'none', roles: [], util: number }
  function skillCoverage(skill, deptId){
    const normSkill = skill.trim().toLowerCase();
    // Find roles in any dept that have this skill
    const orgRoles = S.roles.filter(r=>(r.skills||[]).some(s=>s.trim().toLowerCase()===normSkill));
    if(!orgRoles.length) return { status:'none', roles:[], util:0, msg:`No role in TEA has '${skill}'` };

    // Find roles in the specific dept
    const deptRoles = deptId==='ALL' ? orgRoles : orgRoles.filter(r=>r.dept===deptId);
    if(!deptRoles.length){
      const otherDepts = [...new Set(orgRoles.map(r=>dName(r.dept)))].join(', ');
      return { status:'gap', roles:orgRoles, util:0, msg:`'${skill}' exists in TEA but not in this department (found in: ${otherDepts})` };
    }

    // Staff assigned to those roles — no utilization tier here. This used to compare against the
    // retired legacy hours engine's utilization %, but nothing that reads this result ever checked
    // that tier (every call site only branches on 'none' vs 'gap' vs anything-else) — simplified to
    // match what's actually used rather than compute a number nobody reads.
    const roleIds = new Set(deptRoles.map(r=>r.id));
    const empInRole = S.team.filter(e=>roleIds.has(e.roleId)&&!e.vacant&&!e._builtStale&&(deptId==='ALL'||e.dept===deptId));

    if(!empInRole.length) return { status:'gap', roles:deptRoles, util:0, msg:`Roles with '${skill}' exist but no staff assigned` };
    return { status:'covered', roles:deptRoles, util:0, msg:`'${skill}' covered — ${empInRole.length} staff assigned` };
  }

  // Check all required skills for a demand source (client or pipeline item)
  function checkSkillCoverage(reqSkills, deptId){
    if(!reqSkills||!reqSkills.length) return [];
    return reqSkills.map(skill=>({ skill, ...skillCoverage(skill, deptId||'ALL') }));
  }

  function haircutMult(){
    const h = S.haircut;
    const workingDays = 52*5;
    const offDays = (h.pto||0) + (h.hol||0) + (h.sick||0);
    const avail = Math.max(0, (workingDays - offDays) / workingDays);
    const interrupt = 1 - Math.min((h.interrupt||0)/100, 0.3);
    // Turnover haircut: at X% annual turnover, average employee is present (1 - X/2 × avg_vacancy_weeks/52)
    // Simplified: reduce capacity by turnover%/4 to account for vacancy gaps during the year
    const turnoverHaircut = 1 - Math.min((S.settings.turnover||0)/100/4, 0.1);
    return avail * interrupt * turnoverHaircut;
  }

  // Productivity multipliers by seniority tier
  // A "junior" employee fills the role's hours but delivers less effective output.
  // These multipliers scale the role's pct[] profile before capacity is counted.
  const SEN_MULT = { junior:0.65, mid:0.85, senior:1.00, lead:1.00 };

  // Mentor drag: during a junior ramp period, each senior/lead in the same dept
  // absorbs this fraction of their own billable capacity into overhead coaching.
  // Applied as a temporary overhead inflation — reduces net billable output.
  const MENTOR_DRAG_PER_JUNIOR = 0.08;

  // Departure probability adjustments: lead/senior leave more (promotion, poaching)
  // These scale the raw deptProb set on each employee for display purposes.
  const SEN_DEP_SCALE = { junior:1.0, mid:1.0, senior:1.2, lead:1.3 };

  function seniorityMult(emp) {
    return SEN_MULT[emp.seniority||'mid'] || 1.0;
  }

  // Productive FTE for a department's non-vacant roster — weights part-time hours and actual,
  // temporary ramp status instead of counting every person as one flat FTE. This is deliberately
  // NOT a permanent discount by seniority level: a junior with a completed ramp counts as a full
  // FTE, same as anyone else. Only someone explicitly still inside their ramp window (rampWeeksRemaining
  // > 0) is discounted, using their own role's documented ramp productivity — not a blanket table.
  function empRampMult(e) {
    if (!e.rampWeeksRemaining || e.rampWeeksRemaining <= 0) return 1.0;
    var role = getRole(e.roleId);
    return (role && role.rampProd) ? role.rampProd : 1.0;
  }

  // Fraction of a working year actually available after routine time off — PTO, company holidays,
  // and typical sick time (Configure → Haircuts). Deliberately separate from the full legacy
  // haircutMult(), which also folds in task-switching interruption and turnover — those are
  // different concepts (being at work but context-switching; a future vacancy risk) from simply
  // being out. This is specifically "how much of a working year is this person actually present
  // for" — reusing the SAME org-wide assumption already being maintained for Team Fragility, so
  // this costs zero additional manager input.
  function timeOffAvailabilityMult() {
    var h = S.haircut || {};
    var workingDays = 52 * 5;
    var offDays = (h.pto || 0) + (h.hol || 0) + (h.sick || 0);
    return Math.max(0, (workingDays - offDays) / workingDays);
  }

  // Pre-haircut productive FTE — same exclusions as productiveFTEForTeam, but without the time-off
  // or active-outage discount. Exists so the live signal can show the honest before/after rather
  // than silently changing a number nobody can see the reason for.
  function productiveFTEForTeamRaw(team) {
    return team.reduce(function(s,e){ return (e.vacant || e._builtStale) ? s : s + (((e.hrs!=null?e.hrs:40))/40) * empRampMult(e); }, 0);
  }

  function productiveFTEForTeam(team) {
    // Excludes both vacant seats and _builtStale records — a stale record means Built's own
    // export no longer lists this person on this seat (replaced or departed), so their hours must
    // not count toward productive capacity even though they're still vacant:false on their own
    // (historically accurate) record. This is the single most-called productive-capacity function
    // in the model — fixing it here covers every consumer, rather than relying on each call site
    // to remember the exclusion itself.
    // Also discounts for (a) routine time off — PTO/holidays/sick, applied to everyone equally via
    // timeOffAvailabilityMult, and (b) a CURRENTLY ACTIVE known outage (Manage → Known Outages) for
    // that specific person — someone on an entered leave of absence right now is not available
    // capacity today. Contractor-covered outages count at the same 80%-availability rate used
    // everywhere else outages are modeled (outageMultForEmp); week 1 is this model's existing
    // convention for "the current/nearest period," the same one Known Outages' own impact chart
    // already uses. A ramping employee who is also currently out is correctly reduced by both
    // factors together, not just whichever one a call site happened to remember.
    var timeOffMult = timeOffAvailabilityMult();
    return team.reduce(function(s,e){
      if (e.vacant || e._builtStale) return s;
      var outageMult = outageMultForEmp(e.id, 1);
      return s + (((e.hrs!=null?e.hrs:40))/40) * empRampMult(e) * timeOffMult * outageMult;
    }, 0);
  }

  // FTE-weighted headcount for support-lane ratio math (headcount/requisition/volume lanes). A
  // raw .length count treats a 20-hr part-time person identically to a 40-hr full-time person,
  // which overstates real capacity once part-time hours are tracked accurately (see the Built
  // import fix above) — this sums hrs/40 instead so the ratio reflects actual FTE, not headcount.
  function fteWeightedCount(list) {
    return list.reduce(function(s,e){ return e._builtStale ? s : s + (((e.hrs!=null?e.hrs:40))/40); }, 0);
  }

  // Count how many active juniors are still inside their tracked ramp window and in a ramping
  // state (approximation: scaled by rampProd of their role). A junior whose ramp period has
  // finished (rampWeeksRemaining is unset, zero, or negative) is fully productive and generates
  // no more mentoring drag than anyone else — seniority tag alone isn't a ramp-status proxy.
  function mentorDragForDept(deptId) {
    const juniors = S.team.filter(e => !e.vacant && !e._builtStale && (deptId==='ALL'||e.dept===deptId) && (e.seniority==='junior') && (e.rampWeeksRemaining > 0));
    // Each ramping junior generates drag proportional to how far they are from full productivity
    // (1 - rampProd) × MENTOR_DRAG_PER_JUNIOR hrs per senior/lead
    return juniors.reduce((total, e) => {
      const role = getRole(e.roleId);
      const rampShortfall = role ? (1 - (role.rampProd||0.65)) : 0.35;
      return total + rampShortfall * MENTOR_DRAG_PER_JUNIOR;
    }, 0); // returns a fractional overhead add per senior/lead
  }

  // Adjusted billable capacity for an employee, accounting for seniority mult + mentor drag
  function empBillCapAdjusted(emp, wk) {
    if (emp.vacant || emp._builtStale) return 0;
    const role = getRole(emp.roleId);
    if (!role) return 0;
    const outageMult = wk != null ? outageMultForEmp(emp.id, wk) : 1;
    const hrs = (emp.hrs || role.hrs) * haircutMult() * outageMult;
    const seniMult = seniorityMult(emp);

    // Raw billable capacity adjusted for seniority productivity
    const rawBill = S.acts.reduce((s,a,i) => s + (a.billable ? hrs * (role.pct[i]||0) : 0), 0) * seniMult;

    // Apply mentor drag for senior/lead employees only
    let drag = 0;
    if ((emp.seniority==='senior'||emp.seniority==='lead') && !emp.vacant && !emp._builtStale) {
      const deptDrag = mentorDragForDept(emp.dept);
      drag = rawBill * deptDrag; // hours lost to mentoring juniors
    }
    return Math.max(0, rawBill - drag);
  }

  // Fragility score for a dept (0–100). Higher = more fragile.
  // Factors: % of billable capacity in high-departure-risk staff, seniority concentration
  function calcFragility(deptId) {
    const isAll = deptId==='ALL';
    const emp = S.team.filter(e => !e.vacant && !e._builtStale && (isAll || e.dept===deptId));
    if (!emp.length) return { score:0, riskCapPct:0, tiers:{}, topRisks:[], capAtRisk:0, totalBillCap:0 };

    const totalBillCap = emp.reduce((s,e) => s + empBillCapAdjusted(e), 0);

    // Capacity at risk = billable cap of employees with departure prob > 12%
    const atRiskEmp = emp.filter(e => {
      const scaledProb = (e.deptProb != null ? e.deptProb : 0.15) * (SEN_DEP_SCALE[e.seniority||'mid']||1.0);
      return scaledProb > 0.12;
    });
    const capAtRisk = atRiskEmp.reduce((s,e) => s + empBillCapAdjusted(e), 0);
    const riskCapPct = totalBillCap ? Math.round(capAtRisk/totalBillCap*100) : 0;

    // Tier distribution
    const tiers = {junior:0, mid:0, senior:0, lead:0};
    emp.forEach(e => { const s=e.seniority||'mid'; tiers[s]=(tiers[s]||0)+1; });

    // Concentration score: if >50% of staff are senior+lead, add concentration risk
    const seniorPct = ((tiers.senior||0)+(tiers.lead||0)) / emp.length;
    const concentrationPenalty = Math.max(0, (seniorPct - 0.4) * 60); // 0 if <40%, up to 12 if 60%

    // Junior drag score: if >30% junior, team is burdened with mentoring load
    const juniorPct = (tiers.junior||0) / emp.length;
    const juniorPenalty = Math.max(0, (juniorPct - 0.2) * 50);

    // Base score: % of cap at risk, capped and weighted
    const score = Math.min(100, Math.round(riskCapPct * 0.7 + concentrationPenalty + juniorPenalty));

    // Top 3 risk employees
    const topRisks = emp
      .map(e => ({ ...e, scaledProb: (e.deptProb != null ? e.deptProb : 0.15)*(SEN_DEP_SCALE[e.seniority||'mid']||1.0), billCap: empBillCapAdjusted(e) }))
      .sort((a,b) => b.scaledProb - a.scaledProb)
      .slice(0,3);

    // Simulate departure+junior-hire capacity curve (weeks 1–26)
    // If top 2 at-risk depart, replaced by juniors after avgTTH weeks
    const avgTTH = emp.reduce((s,e) => { const r=getRole(e.roleId); return s+(r?r.tth:12); }, 0) / emp.length;
    // Ramp Period (Configure → Haircuts, role.rampWks) was previously ignored here — the curve
    // used a hardcoded 8-week ramp regardless of what a manager set, so editing that field
    // appeared to do nothing for this specific calculation. A reviewer caught the disconnect;
    // fixed by averaging role.rampWks the same way avgTTH already averages role.tth, with the
    // old hardcoded 8 kept only as the fallback for a role that's never had rampWks set.
    const avgRampWks = emp.reduce((s,e) => { const r=getRole(e.roleId); return s+(r&&r.rampWks?r.rampWks:8); }, 0) / emp.length;
    const top2Cap = topRisks.slice(0,2).reduce((s,e) => s + e.billCap, 0);
    const curve = [];
    for (let wk=1; wk<=26; wk++) {
      let scenarioCap = totalBillCap;
      // Loss: top 2 depart at wk 0
      scenarioCap -= top2Cap;
      // Recovery: junior replacement starts contributing at wk avgTTH at rampProd
      if (wk >= Math.round(avgTTH)) {
        const rampFrac = Math.min(1, (wk - avgTTH) / avgRampWks);
        scenarioCap += top2Cap * 0.65 * rampFrac; // junior at 65% mult
      }
      const util = totalBillCap>0 ? Math.round(scenarioCap/totalBillCap*100) : 0;
      curve.push({ wk, cap: Math.round(scenarioCap*10)/10, pct: util });
    }

    return { score, riskCapPct, tiers, topRisks, capAtRisk:Math.round(capAtRisk*10)/10, totalBillCap:Math.round(totalBillCap*10)/10, seniorPct:Math.round(seniorPct*100), juniorPct:Math.round(juniorPct*100), concentrationPenalty:Math.round(concentrationPenalty), juniorPenalty:Math.round(juniorPenalty), curve, mentorDrag:Math.round(mentorDragForDept(deptId)*100) };
  }

  // Returns outage multiplier for a specific employee at a given week (1 = fully present, 0 = fully absent)
  function outageMultForEmp(empId, wk){
    const active = (S.outages || []).filter(o => o.empId===empId && wk>=o.startWk && wk<o.startWk+o.durationWks);
    if(!active.length) return 1;
    // If covered by contractor, capacity is maintained at contractor productivity level (80%)
    const covered = active.some(o=>o.coverageType==='contractor');
    return covered ? 0.8 : 0;
  }

  // Stubbed by scripts/extract-engine.mjs: presentation only, no calculation.
  function renderTiers() {}

  function addTier() {
    // Collision-safe key generation — the old `'T'+(tiers.length+1)` logic could reproduce an
    // existing key once T1/T2/T3 could be deleted and re-added in a different count (e.g. delete
    // T2, leaving 2 tiers, then add: length+1 = 'T3', already taken). Finds the lowest unused
    // T-number instead, so a fresh tier can never silently collide with one that already exists.
    const existing = S.settings.tiers.map(t => t.key);
    let n = 1;
    while (existing.indexOf('T'+n) !== -1) n++;
    S.settings.tiers.push({key:'T'+n, label:'Tier '+n, priority:'Standard'});
    renderTiers();
  }

  function matchDept(raw){
    if(!raw) return null;
    const r = raw.toLowerCase().trim();
    // Exact match on name or code
    let match = S.depts.find(d => d.name.toLowerCase()===r || d.code.toLowerCase()===r);
    if(match) return match.id;
    // Partial match
    match = S.depts.find(d => r.includes(d.name.toLowerCase()) || d.name.toLowerCase().includes(r) || r.includes(d.code.toLowerCase()));
    return match ? match.id : null;
  }

  function matchRole(raw, deptId){
    if(!raw) return null;
    const r = raw.toLowerCase().trim();
    // Try dept-scoped match first
    const deptRoles = deptId ? S.roles.filter(ro=>ro.dept===deptId) : S.roles;
    const allRoles  = S.roles;
    for(const pool of [deptRoles, allRoles]){
      // Exact
      let m = pool.find(ro => ro.name.toLowerCase()===r);
      if(m) return m.id;
      // Contains
      m = pool.find(ro => r.includes(ro.name.toLowerCase()) || ro.name.toLowerCase().includes(r));
      if(m) return m.id;
      // Keyword overlap (split into words)
      const words = r.split(/\s+/);
      m = pool.find(ro => words.some(w => w.length>3 && ro.name.toLowerCase().includes(w)));
      if(m) return m.id;
    }
    return null;
  }

  // Pure, DOM-free Business Case verdict logic — deliberately factored out of renderBizCase so
  // Run Model Self-Check (see runModelSelfCheck) can call it directly with fixed inputs and assert
  // on the result, without needing a live document, S state, or any other rendering side effect.
  // Two distinct gap tiers, deliberately kept separate:
  //  - hasFlaggedGap requires the department to have actually crossed its own action threshold.
  //    This is the only gate that may drive a dollar comparison or a HIRE recommendation — a
  //    sub-threshold Watch department hasn't earned that level of urgency yet.
  //  - hasRealGap is broader (Watch OR Flagged): a Watch department already has a real, non-zero,
  //    formula-driven shortfall, just below the point the department itself calls urgent. That's
  //    still real enough to justify starting a Position Request now — Built approvals take real
  //    time, and waiting for Flagged before requesting the seat means the position often doesn't
  //    exist yet by the time it's actually needed.
  function computeBizCaseVerdict(sig, opts) {
    var hasEligiblePosition = opts.hasEligiblePosition;
    var financiallyFavorable = opts.financiallyFavorable;
    var hireCostFor12Wks = opts.hireCostFor12Wks;
    var ctrCost = opts.ctrCost;
    var fmt$ = opts.fmt$;

    var hasFlaggedGap = sig && (sig.kind==='volume' || sig.kind==='support') && sig.flagged;
    var hasRealGap = sig && (sig.kind==='volume' || sig.kind==='support') && (sig.flagged || sig.watch);

    var verdict, verdictBasis;
    if (sig && sig.dataInvalid) {
      verdict = 'DATA INVALID — CANNOT ASSESS';
      verdictBasis = 'This department\'s own inputs are invalid: ' + sig.invalidReasons.join('; ') + '. Correct these before any seat gap, cost comparison, or recommendation here can be trusted.';
    } else if (sig && sig.notConfigured) {
      verdict = 'DATA INCOMPLETE — CANNOT ASSESS';
      verdictBasis = 'This department has no roles or roster loaded, so there is nothing to quantify a gap against. Load role and roster data (or a Built import) before requesting a hiring case here.';
    } else if (hasFlaggedGap && hasEligiblePosition) {
      if (financiallyFavorable && hireCostFor12Wks <= ctrCost) {
        verdict = 'REVIEW: HIRE RECOMMENDED';
        verdictBasis = 'Quantified requirement, identified role, and a hire-eligible position all align with the financial case.';
      } else if (ctrCost < hireCostFor12Wks) {
        verdict = 'CONTRACTOR FIRST';
        verdictBasis = 'A real gap and an eligible position exist, but over the same 12-week bridge window a contractor costs less than the prorated hire cost ('+fmt$(ctrCost)+' vs '+fmt$(hireCostFor12Wks)+') — reassess in 6 weeks.';
      } else {
        verdict = 'MONITOR';
        verdictBasis = 'A position exists to fill the gap, but the dollar case does not yet justify urgency.';
      }
    } else if (hasRealGap && !hasEligiblePosition) {
      verdict = 'POSITION REQUEST RECOMMENDED';
      verdictBasis = sig.flagged
        ? 'A real gap is identified, but there is no approved/hire-eligible position yet — take this to Built before recruiting.'
        : 'A real, formula-driven gap exists — currently Watch, below this department\'s own action threshold — and Built has no eligible position yet. Starting the Position Request now avoids a delay if this crosses to Flagged before an approval comes through.';
    } else if (hasRealGap && hasEligiblePosition) {
      verdict = 'MONITOR';
      verdictBasis = 'A sub-threshold (Watch) gap exists and an eligible position is already available — not yet urgent enough for a dollar comparison, but there is already a seat to fill if that changes.';
    } else if (sig && sig.kind==='coverage' && sig.flagged) {
      verdict = 'OPERATIONAL INTERVENTION REQUIRED';
      verdictBasis = 'A coverage gap is real, but coverage does not reduce to a seat count — see the Coverage Map, not a hire.';
    } else if (sig && sig.managerEscalated) {
      verdict = 'FURTHER DIAGNOSIS REQUIRED';
      verdictBasis = 'A manager has flagged a concern, but there is not yet enough evidence here to identify the right fix — a manager escalation is a prompt to review, not financial proof of a hire.';
    } else {
      verdict = 'MONITOR';
      verdictBasis = 'Insufficient evidence of a sustained gap to recommend action.';
    }
    return { verdict: verdict, verdictBasis: verdictBasis, hasFlaggedGap: hasFlaggedGap, hasRealGap: hasRealGap };
  }

  // Pure, DOM-free revenue-at-risk calculation — extracted specifically so the Executive Summary
  // view can show the identical figure Business Case shows, rather than a second, independently
  // computed number that could quietly drift from it. Scoped directly by each client's own
  // department field, so internal-only work (payroll, comp programs, employee population) never
  // gets attributed generic client revenue. Internal demand sources are explicitly tagged
  // revenueBearing:false — they don't generate client revenue at all, so a dollar "revenue at
  // risk" figure for them is fictional, not just imprecise.
  function computeRevenueAtRisk(deptId, isFlagged, isWatch) {
    var C = S.costs;
    var scopedClients = S.clients.filter(function(c) { return deptId === 'ALL' || c.dept === deptId; });
    var revenueBearingClients = scopedClients.filter(function(c) { return c.revenueBearing !== false; });
    var internalOnlyScope = scopedClients.length > 0 && revenueBearingClients.length === 0;
    var revAtRisk = revenueBearingClients.reduce(function(s, c) {
      var rev = C[c.tier === 'T1' ? 't1Rev' : c.tier === 'T2' ? 't2Rev' : 't3Rev'] || 0;
      return s + rev;
    }, 0);
    var churnRate = isFlagged ? (C.churnCrit || 25) / 100 : isWatch ? (C.churnElev || 10) / 100 : 0.02;
    return internalOnlyScope ? 0 : r1(revAtRisk * churnRate);
  }

  // ── Trading Desk Coverage Map ─────────────────────────────────────────────
  // Coverage answers the intraday-demand question utilization math can't:
  // who is on which desk, in which time block. 0 = uncovered, 1 = single-point,
  // 2+ = resilient, — = not applicable to that desk.
  var TRADE_COV_ROWS = ['Power Day-Ahead — East','Power Day-Ahead — West','Real-Time — East','Real-Time — West','Natural Gas','Portfolio Mgmt — East','Portfolio Mgmt — West','Power Supply Planning'];

  var TRADE_COV_BLOCKS = ['Pre-Market<br>5–8a','Morning<br>8–11a','Midday Peak<br>11a–1p','Afternoon<br>1–4p','Evening RT<br>4–10p','Overnight RT<br>10p–5a'];

  var TRADE_COV_DEFAULT = [
    [1,2,2,1,-1,-1],
    [1,1,2,1,-1,-1],
    [1,1,1,1,1,1],
    [1,1,2,1,1,1],
    [1,2,1,1,-1,-1],
    [1,1,2,1,-1,-1],
    [1,1,1,1,-1,-1],
    [2,2,1,1,-1,-1]
  ];

  // Maps a department to its Trading Coverage Map row, so tiles can pull a live coverage summary.
  var COVERAGE_DEPT_ROW = { d_tde:0, d_tdw:1, d_rte:2, d_rtw:3, d_ngt:4, d_pme:5, d_pmw:6, d_psp:7 };

  var COVERAGE_DEPT_IDS = Object.keys(COVERAGE_DEPT_ROW);

  // Secondary reference volumes for coverage desks — informational only, coverage remains the primary signal.
  var COVERAGE_VOL_REF = {
    d_tde:{name:'Trades executed / month', current:5200}, d_tdw:{name:'Trades executed / month', current:4700},
    d_rte:{name:'Real-time trades / month', current:8100}, d_rtw:{name:'Real-time trades / month', current:7400},
    d_ngt:{name:'Gas trades / month', current:3100},
    d_pme:{name:'Books under management', current:9}, d_pmw:{name:'Books under management', current:7},
    d_psp:{name:'Supply plans issued / month', current:14}
  };

  function getTradeCov(r,c){
    S.tradeCov = S.tradeCov || {};
    var k = r+'_'+c;
    return (S.tradeCov[k] != null) ? S.tradeCov[k] : TRADE_COV_DEFAULT[r][c];
  }

  // Single source of truth for a department's flag status under the v19+ model (current build: v20).
  // Returns {flagged (red), watch (amber), kind, volGap, covUncovered, covSingle} — used by
  // the Overview KPI strip AND the per-department tiles so the two can never show conflicting numbers.
  function applyManagerEscalation(dep, result) {
    var msig = S.managerSignals && S.managerSignals[dep.id];
    if (!msig) return result;
    var behind = msig.completeness === 'behind';
    var below  = msig.quality === 'below';
    var weak   = msig.mastery === 'D' || msig.mastery === 'F';
    // Documented escalation rule (not a scored formula): completeness AND quality both bad,
    // or completeness/quality bad alongside a weak mastery grade, escalates to flagged.
    // Any single bad manager read escalates to at least watch.
    var managerFlags = (behind && below) || (weak && (behind || below));
    var managerWatch = behind || below || weak;
    if (managerFlags && !result.flagged) {
      result = Object.assign({}, result, { flagged: true, managerEscalated: true,
        managerReason: 'Manager reports ' + [behind&&'work falling behind', below&&'quality below standard', weak&&'weak team mastery'].filter(Boolean).join(' and ') });
    } else if (managerWatch && !result.flagged && !result.watch) {
      result = Object.assign({}, result, { watch: true, managerEscalated: true,
        managerReason: 'Manager reports ' + [behind&&'work falling behind', below&&'quality below standard', weak&&'weak team mastery'].filter(Boolean).join(' and ') });
    }
    return result;
  }

  var VOL_PERIOD_BASIS_LABELS = {
    current: 'single period',
    rolling3: '3-month rolling avg',
    seasonalPeak: 'same period last year',
    forecastSustained: 'forecasted sustained',
    consecutiveN: 'consecutive periods',
  };

  var MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  var PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }

  function parsePeriodStr(str) {
    if (!str || !PERIOD_RE.test(str)) return null;
    var parts = str.split('-');
    return { year: parseInt(parts[0], 10), month0: parseInt(parts[1], 10) - 1 };
  }

  function addMonthsToPeriod(period, delta) {
    var total = period.year * 12 + period.month0 + delta;
    var y = Math.floor(total / 12), m0 = ((total % 12) + 12) % 12;
    return y + '-' + pad2(m0 + 1);
  }

  function formatPeriodLabel(str) {
    var p = parsePeriodStr(str);
    return p ? (MONTH_NAMES[p.month0] + ' ' + p.year) : str;
  }

  // Validates and canonicalizes a department's recorded period history plus its explicit current
  // reporting period — run ONCE per department (not once per driver, since dates are shared across
  // every driver on that department) so the Data Invalid gate reports each distinct problem exactly
  // once rather than once per driver. Checks, specifically because independent testing found the
  // prior version silently accepted all of these:
  //  - the current reporting period is an explicit stored field, never the computer's clock — a
  //    manager may be entering data days or weeks after the period it describes, and "seasonal
  //    peak" in particular is meaningless if it's anchored to whatever day happens to load the page.
  //  - every recorded period has a real, valid YYYY-MM date (not blank, not free text)
  //  - no two recorded periods share a date
  //  - no recorded period is dated to the current reporting period itself (that reading already
  //    comes from the driver's live "current" value, not from a history row)
  // Returns { valid, errors, sortedHistory (chronological, oldest first), currentPeriod (parsed
  // {year,month0}, or null) }. Basis-specific functions below look up history by EXACT calendar
  // month via this sorted/validated result — never by raw array position — so an out-of-order,
  // undated, or non-consecutive entry can no longer be silently treated as if it were adjacent to
  // today, which is the exact failure mode reported.
  function validateVolPeriodHistory(dep) {
    var errors = [];
    var basis = dep.volPeriodBasis || 'current';
    var needsPeriodMath = (basis === 'rolling3' || basis === 'seasonalPeak' || basis === 'consecutiveN');

    var currentPeriod = null;
    if (needsPeriodMath) {
      currentPeriod = parsePeriodStr(dep.volCurrentPeriod);
      if (!currentPeriod) {
        errors.push('the current reporting period is not set (required for the "' + VOL_PERIOD_BASIS_LABELS[basis] + '" basis) — set it in Time-Period Basis Calibration');
      }
    }

    var raw = dep.volPeriodHistory || [];
    var seen = {};
    var validEntries = [];
    raw.forEach(function(p, idx) {
      if (!p) return;
      if (!needsPeriodMath) return; // dates only matter for date-relative bases
      if (!p.date || !PERIOD_RE.test(p.date)) {
        errors.push('period history row ' + (idx + 1) + ' has a missing or invalid month');
        return;
      }
      if (currentPeriod && p.date === dep.volCurrentPeriod) {
        errors.push('period history has a row dated ' + formatPeriodLabel(p.date) + ', which is the current reporting period — that reading already comes from the driver\'s live value above, not from a history row');
        return;
      }
      if (seen[p.date]) {
        errors.push('period history has more than one row dated ' + formatPeriodLabel(p.date) + ' — dates must be unique');
        return;
      }
      seen[p.date] = true;
      validEntries.push(p);
    });
    validEntries.sort(function(a, b) { return a.date < b.date ? -1 : a.date > b.date ? 1 : 0; });

    return { valid: errors.length === 0, errors: errors, sortedHistory: validEntries, currentPeriod: currentPeriod };
  }

  // Resolves the single number that feeds the FTE-implied calculation for one volume driver,
  // according to the department's configured period basis. `periodInfo` is the (optional,
  // recommended) result of validateVolPeriodHistory(dep) — pass it explicitly when looping over
  // several drivers so the department-level validation isn't repeated per driver; if omitted it's
  // computed internally so every call site still gets correct behavior on its own.
  // Returns { value, insufficient, reason }. `insufficient: true` means there isn't enough valid
  // history or forecast data to honor the configured basis — that gates the whole department to
  // Data Invalid rather than silently substituting the plain current reading, which would quietly
  // defeat the entire point of calibrating a basis in the first place.
  function resolveVolumeDriverValue(dep, vi, vd, periodInfo) {
    var basis = dep.volPeriodBasis || 'current';
    var curOverride = (S.deptVolumes && S.deptVolumes[dep.id] && S.deptVolumes[dep.id][vi] != null) ? S.deptVolumes[dep.id][vi] : vd.current;
    if (!periodInfo) periodInfo = validateVolPeriodHistory(dep);

    if (basis === 'rolling3' || basis === 'seasonalPeak' || basis === 'consecutiveN') {
      if (!periodInfo.valid) {
        return { value: null, insufficient: true, reason: 'driver "' + vd.name + '": the recorded period history has unresolved date problems — see Time-Period Basis Calibration for details' };
      }
    }

    if (basis === 'rolling3') {
      var cp = periodInfo.currentPeriod;
      var m1date = addMonthsToPeriod(cp, -1), m2date = addMonthsToPeriod(cp, -2);
      var byDate1 = {}; periodInfo.sortedHistory.forEach(function(p){ byDate1[p.date] = p; });
      var e1 = byDate1[m1date], e2 = byDate1[m2date];
      var v1 = e1 && e1.values ? e1.values[vi] : undefined;
      var v2 = e2 && e2.values ? e2.values[vi] : undefined;
      var missing = [];
      if (typeof v1 !== 'number' || !isFinite(v1)) missing.push(formatPeriodLabel(m1date));
      if (typeof v2 !== 'number' || !isFinite(v2)) missing.push(formatPeriodLabel(m2date));
      if (missing.length || typeof curOverride !== 'number' || !isFinite(curOverride)) {
        return { value: null, insufficient: true, reason: 'driver "' + vd.name + '" is set to a 3-month rolling average but is missing a recorded value for ' + (missing.length ? missing.join(' and ') : 'the current period') + ' — the two months immediately before the current reporting period, with no gaps' };
      }
      return { value: (v1 + v2 + curOverride) / 3, insufficient: false };
    }
    if (basis === 'seasonalPeak') {
      // "Same period last year" — a stable seasonal reference point, anchored to the department's
      // explicit current reporting period, not whatever today's calendar date happens to be and not
      // simply the highest value ever recorded either. Requires a period explicitly dated to this
      // same calendar month one year before the current reporting period; nothing else qualifies.
      var cp2 = periodInfo.currentPeriod;
      var targetDate = (cp2.year - 1) + '-' + pad2(cp2.month0 + 1);
      var match = periodInfo.sortedHistory.filter(function(p){ return p.date === targetDate; }).pop();
      var matchVal = match && match.values ? match.values[vi] : undefined;
      if (typeof matchVal !== 'number' || !isFinite(matchVal)) {
        return { value: null, insufficient: true, reason: 'driver "' + vd.name + '" is set to seasonal peak (same period last year) but no period is recorded for ' + formatPeriodLabel(targetDate) };
      }
      return { value: matchVal, insufficient: false };
    }
    if (basis === 'forecastSustained') {
      var fv = (dep.volForecastValues || [])[vi];
      if (typeof fv !== 'number' || !isFinite(fv) || fv <= 0) {
        return { value: null, insufficient: true, reason: 'driver "' + vd.name + '" is set to a forecasted sustained increase but has no valid forecast value entered' };
      }
      return { value: fv, insufficient: false };
    }
    // 'current' and 'consecutiveN' both use the plain current reading for the FTE-implied number
    // itself — consecutiveN's distinct behavior lives entirely in whether Flagged is allowed to
    // fire (see checkConsecutivePeriods), not in which number is used here.
    if (typeof curOverride !== 'number' || !isFinite(curOverride) || curOverride < 0) {
      return { value: null, insufficient: true, reason: 'driver "' + vd.name + '" has an invalid volume (' + curOverride + ')' };
    }
    return { value: curOverride, insufficient: false };
  }

  // For the 'consecutiveN' basis only: walks backward from the department's explicit current
  // reporting period — current, current-1mo, current-2mo, ... — recomputing each period's combined
  // implied FTE with the SAME combine mode, per-FTE rates, and action threshold configured today,
  // and checks whether the gap has been at or over threshold for N *calendar-consecutive* periods
  // running. Each month in the chain must have an exact, individually-dated entry in the validated
  // history (via periodInfo, see validateVolPeriodHistory) — a missing month breaks the streak
  // immediately, so non-consecutive or out-of-order dates can no longer be silently accepted as if
  // they were adjacent, which is the exact failure mode reported. This deliberately compares every
  // historical period against TODAY's productive FTE and TODAY's rates/threshold — it answers "has
  // this same current staffing level and these same thresholds been genuinely insufficient for N
  // periods in a row", not "how did staffing change over time" (which this model does not track).
  function checkConsecutivePeriods(dep, currentValues, combineMode, productiveFTE, volTrig, periodInfo) {
    var n = (typeof dep.volConsecutiveN === 'number' && dep.volConsecutiveN >= 1) ? Math.round(dep.volConsecutiveN) : 3;
    if (!periodInfo) periodInfo = validateVolPeriodHistory(dep);
    if (!periodInfo.valid || !periodInfo.currentPeriod) return { streak: 0, required: n, sufficient: false };

    var byDate = {}; periodInfo.sortedHistory.forEach(function(p){ byDate[p.date] = p.values; });
    var cp = periodInfo.currentPeriod;

    var streak = 0;
    for (var k = 0; k < n; k++) {
      var vals = (k === 0) ? currentValues : byDate[addMonthsToPeriod(cp, -k)];
      if (!vals || vals.length !== dep.volDrivers.length) break; // missing/misaligned month breaks the streak
      var bad = false;
      var raw = dep.volDrivers.map(function(vd, vi) {
        var v = vals[vi];
        if (typeof v !== 'number' || !isFinite(v) || v < 0 || !(vd.perFTE > 0)) { bad = true; return 0; }
        return v / vd.perFTE;
      });
      if (bad) break;
      var impliedThisPeriod = combineMode === 'sum'
        ? Math.ceil(raw.reduce(function(s,x){ return s+x; }, 0))
        : Math.ceil(raw.reduce(function(m,x){ return x>m?x:m; }, 0));
      var gapThisPeriod = impliedThisPeriod - productiveFTE;
      if (gapThisPeriod >= volTrig) streak++;
      else break;
    }
    return { streak: streak, required: n, sufficient: streak >= n };
  }

  // Active Projects are wired into the live signal for volume-threshold departments (added to
  // implied FTE, see computeDeptSignal below). Coverage, support, and judgment departments don't
  // have a natural, honest way to fold "extra hours from a temporary project" into their own
  // formulas — coverage is about WHO covers WHEN, support is ratio-based, judgment has no formula
  // at all — forcing a conversion there would invent a mechanism nobody actually asked for. They
  // still show active projects as visible context instead of silently ignoring them, which is
  // what every department type did before any of this was wired up.
  function activeProjectsNote(deptId) {
    var projects = (S.impls || []).filter(function(im) { return im.dept === deptId && im.wksLeft > 0; });
    if (!projects.length) return '';
    var hrs = projects.reduce(function(s, im) { return s + (im.hrs || 0); }, 0);
    return '<div style="font-size:10px;color:var(--ink4);margin-top:5px;padding-top:5px;border-top:1px dashed var(--border)">🔧 ' + projects.length + ' active project' + (projects.length!==1?'s':'') + ' (' + hrs + ' hrs/wk) — <span style="cursor:pointer;color:var(--teal);text-decoration:underline" onclick="switchTab(\'manage\');showManageSection(\'impls\')">view</span>. Not reflected in this department\'s status — there\'s no honest way to fold temporary project hours into this signal type the way volume-threshold departments can.</div>';
  }

  // Anticipated Demand (Pipeline) — same "never silently invisible" rule as Active Projects, but
  // for coverage/support/judgment departments there's an EXTRA reason to keep it out of the
  // formula beyond just "no honest conversion exists": pipeline is probabilistic future business,
  // not confirmed current work, so even volume-threshold departments only get a separate forward
  // figure, never a blend into today's status (see computeDeptSignal). Shown as visible context
  // here for the same reason it's visible there — informational, never silently dropped.
  function pipelineNote(deptId) {
    var horizonWks = (S.settings && S.settings.hz) || 12;
    var items = (S.pipeline || []).filter(function(p) { return p.dept === deptId && p.closeWk != null && p.closeWk <= horizonWks; });
    if (!items.length) return '';
    var wtdHrs = items.reduce(function(s, p) { return s + (p.svcHrs || 0) * (p.prob != null ? p.prob : 1); }, 0);
    return '<div style="font-size:10px;color:var(--ink4);margin-top:5px;padding-top:5px;border-top:1px dashed var(--border)">🎯 ' + items.length + ' anticipated deal' + (items.length!==1?'s':'') + ' expected to close within ' + horizonWks + ' wks (~' + Math.round(wtdHrs) + ' probability-weighted hrs/wk) — <span style="cursor:pointer;color:var(--teal);text-decoration:underline" onclick="switchTab(\'manage\');showManageSection(\'pipeline\')">view</span>. Forward-looking only — not reflected in this department\'s current status, and not folded into a formula this signal type doesn\'t have.</div>';
  }

  function computeDeptSignal(dep) {
    var hasVolDrivers = !!(dep.volDrivers && dep.volDrivers.length);
    var isCoverageDept = COVERAGE_DEPT_IDS.indexOf(dep.id) !== -1;
    var isSupport = (dep.type || 'service') === 'support';
    var isJudgment = (dep.type || 'service') === 'judgment';
    var deptTeam = S.team.filter(function(e){ return e.dept === dep.id && !e._builtStale; });
    var filledCount = deptTeam.filter(function(e){ return !e.vacant && !e._builtStale; }).length;

    // Not configured — zero roles or zero roster means a volume/coverage/support department is
    // missing the data its formula needs, not proven to have a staffing shortage. This gate only
    // applies to formula-driven buckets: a judgment department correctly has no formula regardless
    // of roster size, so "no staff yet" there is already the honest, non-alarming "informational,
    // by design" message — recategorizing it as "not configured" would be a false alarm in the
    // other direction, and would incorrectly flag legitimate zero-staff rollup parents.
    var deptRoleCount = S.roles.filter(function(r){ return r.dept === dep.id; }).length;
    if ((hasVolDrivers || isCoverageDept || isSupport) && (deptRoleCount === 0 || deptTeam.length === 0)) {
      return { kind: 'notConfigured', notConfigured: true, dataIncomplete: true, flagged: false, watch: false,
        wouldBeKind: hasVolDrivers ? 'volume' : isCoverageDept ? 'coverage' : 'support' };
    }

    // Data Invalid — Cannot Assess: distinct from Not Configured. The department HAS data, but
    // some of it is nonsensical for the formula it needs (a zero/negative per-FTE rate, a
    // negative volume, a non-positive threshold or benchmark, or a real employee's hrs/wk that's
    // 0 or otherwise out of range). A warning banner elsewhere in the app is not enough on its
    // own — this department's tile must not be able to read Healthy while its own inputs are
    // broken, since that's exactly the scenario that let a bad per-FTE rate silently produce a
    // clean status. This only applies to volume and support types — coverage's signal is driven
    // entirely by the manually-set grid, not by a rate/threshold/hours input that can be invalid
    // in this sense.
    if (hasVolDrivers || isSupport) {
      var invalidReasons = [];
      // Every check here uses explicit finite-number validation rather than a bare `<= 0` test.
      // `<= 0` alone lets a NaN slip straight through (any comparison against NaN is false), and
      // NaN is exactly what a malformed or blank imported numeric field parses to — so a check
      // that only catches zero/negative values would still let genuinely broken data drive a
      // real calculation. Every one of these must be a real, positive, finite number or the
      // department gates to Data Invalid instead of silently falling back to a hardcoded default.
      var periodInfo = hasVolDrivers ? validateVolPeriodHistory(dep) : null;
      if (hasVolDrivers) {
        // Date/period problems are department-level (dates are shared across every driver), so
        // they're reported once here rather than once per driver below.
        if (!periodInfo.valid) periodInfo.errors.forEach(function(e){ invalidReasons.push(e); });
        dep.volDrivers.forEach(function(vd, vi) {
          if (typeof vd.perFTE !== 'number' || !isFinite(vd.perFTE) || vd.perFTE <= 0) invalidReasons.push('driver "' + vd.name + '" has a per-FTE rate of ' + vd.perFTE + ' (must be a positive number)');
          if (periodInfo.valid) {
            var resolved = resolveVolumeDriverValue(dep, vi, vd, periodInfo);
            if (resolved.insufficient) invalidReasons.push(resolved.reason);
          }
        });
        if (dep.fteTrig != null && (typeof dep.fteTrig !== 'number' || !isFinite(dep.fteTrig) || dep.fteTrig <= 0)) invalidReasons.push('FTE action threshold is ' + dep.fteTrig + ' (must be a positive number)');
      }
      if (isSupport) {
        var __bm = (dep.benchmark != null) ? dep.benchmark : (S.settings ? S.settings.hrBenchmarkRatio : null);
        if (__bm != null && (typeof __bm !== 'number' || !isFinite(__bm) || __bm <= 0)) invalidReasons.push('staffing benchmark ratio is ' + __bm + ' (must be a positive number)');

        // Organization headcount feeds every support-lane ratio directly (headcount, requisition,
        // and volume/step-function lanes all divide by or against it). A missing, zero, negative,
        // or non-numeric value previously fell back to a hardcoded 335 with no visible warning —
        // that's a guess standing in for a verified org size, not a safe default.
        var __orgHC = S.settings ? S.settings.orgHeadcount : null;
        if (typeof __orgHC !== 'number' || !isFinite(__orgHC) || __orgHC <= 0) invalidReasons.push('organization headcount is ' + __orgHC + ' (must be a positive number)');

        var __gateDeptRoles = S.roles.filter(function(r){ return r.dept === dep.id; });
        __gateDeptRoles.filter(function(r){ return r.supportMeta && r.supportMeta.driver === 'requisition'; }).forEach(function(role){
          var rc = role.supportMeta.reqCapacity;
          if (typeof rc !== 'number' || !isFinite(rc) || rc <= 0) invalidReasons.push('"' + role.name + '" has a requisition capacity of ' + rc + ' (must be a positive number)');
        });
        __gateDeptRoles.filter(function(r){ return r.supportMeta && r.supportMeta.driver === 'volume'; }).forEach(function(role){
          var ceil = role.supportMeta.hcCeiling;
          if (typeof ceil !== 'number' || !isFinite(ceil) || ceil <= 0) invalidReasons.push('"' + role.name + '" has a payroll/headcount ceiling of ' + ceil + ' (must be a positive number)');
        });
      }
      deptTeam.forEach(function(e) {
        if (!e.vacant && !e._builtStale && (typeof e.hrs !== 'number' || !isFinite(e.hrs) || e.hrs <= 0 || e.hrs > 60)) invalidReasons.push('"' + e.name + '" has ' + (e.hrs==null?'no':e.hrs) + ' hrs/wk on record');
      });
      if (invalidReasons.length) {
        return { kind: 'dataInvalid', dataInvalid: true, notConfigured: false, flagged: false, watch: false,
          invalidReasons: invalidReasons, wouldBeKind: hasVolDrivers ? 'volume' : 'support' };
      }
    }

    if (hasVolDrivers) {
      // Combine mode is explicit and per-department, defaulting to 'max' so no existing department's
      // output changes silently. 'max' treats multiple drivers as alternative measures of the same
      // underlying workload (pick the more binding one). 'sum' treats them as genuinely separate,
      // additive work streams. Only a department owner who actually knows the work should switch
      // this — the two methods can produce wildly different implied FTE, so both are always computed
      // and exposed, not just the active one, so the tradeoff is visible even before switching.
      var combineMode = dep.volCombineMode === 'sum' ? 'sum' : 'max';
      // The resolved value per driver reflects the department's configured period basis (plain
      // current reading by default — see resolveVolumeDriverValue). Data sufficiency for the
      // configured basis was already confirmed by the Data Invalid gate above, so every resolved
      // value here is safe to use directly.
      var resolvedDriverValues = dep.volDrivers.map(function(vd, vi) { return resolveVolumeDriverValue(dep, vi, vd, periodInfo).value; });
      // Raw (unrounded) per-driver requirement — used for combining. Rounding each driver to a whole
      // FTE independently, THEN summing, overstates the true requirement by up to ~1 FTE per extra
      // driver (e.g. 5.1 and 5.1 independently ceil to 6+6=12, when the real combined need is
      // ceil(10.2)=11). Round exactly once, after combining, not before.
      var driverNeedsRaw = dep.volDrivers.map(function(vd, vi) {
        var cur = resolvedDriverValues[vi];
        return vd.perFTE > 0 ? (cur / vd.perFTE) : 0;
      });
      // Still shown per-driver, rounded, for the individual driver readout in the detail panel —
      // this whole-FTE-per-driver figure is legitimate on its own; it's only summing pre-rounded
      // values that was wrong.
      var driverNeeds = driverNeedsRaw.map(function(n){ return Math.ceil(n); });
      var impliedFTE_max = Math.ceil(driverNeedsRaw.reduce(function(m,n){ return n > m ? n : m; }, 0));
      var impliedFTE_sum = Math.ceil(driverNeedsRaw.reduce(function(s,n){ return s + n; }, 0));
      var impliedFTE_fromDrivers = combineMode === 'sum' ? impliedFTE_sum : impliedFTE_max;
      // Active Projects (Team & Workload → Active Projects) — time-bounded work with a defined end. Its
      // hours add to this department's TOTAL implied demand on top of whatever its volume drivers
      // imply: a project is real, if temporary, work, and "implied FTE" should mean everything
      // currently driving demand, not just the recurring volume-driver portion of it. Summed
      // first, then rounded to 2 decimals — NOT ceiled to a whole FTE. An earlier version ceiled
      // this component the same way the driver combine does, on the reasoning that "you can't
      // hire a fraction of a person" — but that reasoning doesn't hold for a temporary addend:
      // a reviewer correctly caught that it meant a single 1-hour/week project added a full
      // phantom FTE of demand, identical to a 40-hour/week project. Kept as its own separate
      // rounding event (not folded into the driver ceiling) because project hours still aren't
      // measured in the same units as a volume driver — this is a units decision, not a
      // magnitude one. A project whose wksLeft has reached zero contributes nothing.
      var activeProjects = (S.impls || []).filter(function(im) { return im.dept === dep.id && im.wksLeft > 0; });
      var projectHrs = activeProjects.reduce(function(s, im) { return s + (im.hrs || 0); }, 0);
      var impliedFTE_fromProjects = projectHrs > 0 ? Math.round((projectHrs / 40) * 100) / 100 : 0;
      var impliedFTE = Math.round((impliedFTE_fromDrivers + impliedFTE_fromProjects) * 100) / 100;
      // Anticipated Demand (Pipeline) — probabilistic FUTURE business, not confirmed current work
      // the way Active Projects is. Blending an uncertain, not-yet-closed deal directly into
      // TODAY's implied FTE would let speculative business flip a department's status and
      // potentially trigger a real hiring decision over something that might not happen — exactly
      // the kind of fabricated certainty this model exists to avoid (see Data Invalid, Formula vs
      // Judgment). So it is deliberately NOT added to impliedFTE/volGap/flagged/watch. Instead it's
      // exposed as a separate, clearly-labeled forward-looking figure: probability-weighted
      // (hours × close probability — the same expected-value convention the legacy engine already
      // uses), scoped to items expected to close within the model's own Forward Planning Horizon.
      var pipelineHorizonWks = (S.settings && S.settings.hz) || 12;
      var nearTermPipeline = (S.pipeline || []).filter(function(p) { return p.dept === dep.id && p.closeWk != null && p.closeWk <= pipelineHorizonWks; });
      var pipelineWeightedHrs = nearTermPipeline.reduce(function(s, p) { return s + (p.svcHrs || 0) * (p.prob != null ? p.prob : 1); }, 0);
      var pipelineForwardFTE = pipelineWeightedHrs > 0 ? Math.round((pipelineWeightedHrs / 40) * 100) / 100 : 0;
      var forwardImpliedFTE = Math.round((impliedFTE + pipelineForwardFTE) * 100) / 100;
      // Compare against productive FTE, not raw headcount — a part-time or still-ramping
      // employee isn't a full FTE against a volume threshold calibrated on full productive output.
      var productiveFTE_raw = Math.round(productiveFTEForTeamRaw(deptTeam) * 100) / 100;
      var productiveFTE = Math.round(productiveFTEForTeam(deptTeam) * 100) / 100;
      var productiveFTE_timeOffReduction = Math.round((productiveFTE_raw - productiveFTE) * 100) / 100;
      var volGap = Math.round((impliedFTE - productiveFTE) * 100) / 100;
      // Use the department's own configured FTE threshold (fteTrig) rather than flagging any
      // positive gap at all — a 0.01 FTE shortfall isn't the same event as a full seat short.
      // Healthy: gap <= 0. Watch: positive but under threshold. Flagged: at or over threshold.
      var volTrig = (typeof dep.fteTrig === 'number') ? dep.fteTrig : 0.5;
      var volFlagged = volGap >= volTrig;
      var volWatch = !volFlagged && volGap > 0;

      // 'consecutiveN' basis: a real, over-threshold gap is not enough on its own to Flag — it
      // must have held for N periods running (see checkConsecutivePeriods). Short of that, the
      // department reports Watch instead of Flagged: the gap is real and visible, just not yet
      // confirmed sustained. This is the one basis that changes the flagged/watch decision itself
      // rather than which number feeds the FTE-implied calculation.
      var consecutiveCheck = null;
      if ((dep.volPeriodBasis || 'current') === 'consecutiveN') {
        consecutiveCheck = checkConsecutivePeriods(dep, resolvedDriverValues, combineMode, productiveFTE, volTrig, periodInfo);
        if (volFlagged && !consecutiveCheck.sufficient) {
          volFlagged = false;
          volWatch = true;
        }
      }

      return applyManagerEscalation(dep, { kind:'volume', flagged: volFlagged, watch: volWatch, volGap: volGap, impliedFTE: impliedFTE, filledCount: filledCount, productiveFTE: productiveFTE,
        combineMode: combineMode, impliedFTE_max: impliedFTE_max, impliedFTE_sum: impliedFTE_sum, driverNeeds: driverNeeds, multiDriver: dep.volDrivers.length > 1,
        impliedFTE_fromDrivers: impliedFTE_fromDrivers, impliedFTE_fromProjects: impliedFTE_fromProjects, activeProjectCount: activeProjects.length,
        productiveFTE_raw: productiveFTE_raw, productiveFTE_timeOffReduction: productiveFTE_timeOffReduction,
        pipelineForwardFTE: pipelineForwardFTE, forwardImpliedFTE: forwardImpliedFTE, pipelineItemCount: nearTermPipeline.length, pipelineHorizonWks: pipelineHorizonWks,
        volCombineLocked: !!dep.volCombineLocked, volPeriodBasis: dep.volPeriodBasis || 'current',
        consecutiveStreak: consecutiveCheck ? consecutiveCheck.streak : undefined,
        consecutiveRequired: consecutiveCheck ? consecutiveCheck.required : undefined,
        consecutiveSufficient: consecutiveCheck ? consecutiveCheck.sufficient : undefined });
    }
    if (isCoverageDept) {
      var covRow = COVERAGE_DEPT_ROW[dep.id];
      var covUncovered = 0, covSingle = 0;
      for (var ci = 0; ci < TRADE_COV_BLOCKS.length; ci++) {
        var cv = getTradeCov(covRow, ci);
        if (cv === 0) covUncovered++; else if (cv === 1) covSingle++;
      }
      // The coverage grid is manually seeded and not yet tied to the real roster. The upfront
      // not-configured gate above already catches "no roster at all." This handles the distinct,
      // still-real case of an approved, configured department (has roster entries) where every
      // seat is currently vacant — that's a genuine crisis, not a data gap, and must never read
      // as healthy or thin-margin just because someone manually marked grid cells as covered.
      var coverageStaffCount = deptTeam.filter(function(e){ return !e.vacant && !e._builtStale; }).length;
      if (coverageStaffCount === 0) {
        return applyManagerEscalation(dep, { kind:'coverage', flagged: true, watch: false, covUncovered: covUncovered, covSingle: covSingle, allSeatsVacant: true });
      }
      return applyManagerEscalation(dep, { kind:'coverage', flagged: covUncovered > 0, watch: covUncovered === 0 && covSingle > 0, covUncovered: covUncovered, covSingle: covSingle });
    }
    if (isSupport) {
      var orgHC = S.settings.orgHeadcount || 335;
      var benchmark = dep.benchmark || S.settings.hrBenchmarkRatio || 60;
      var deptRoles = S.roles.filter(function(r){ return r.dept === dep.id; });
      var hcRoles = deptRoles.filter(function(r){ return !r.supportMeta || r.supportMeta.driver === 'headcount'; });
      // No "assume 1" fallback — a role with zero actual people contributes zero real capacity.
      // Silently treating an empty lane as "at least one seat" hides genuine unstaffed risk.
      var hcStaff = 0;
      hcRoles.forEach(function(role){ hcStaff += fteWeightedCount(S.team.filter(function(e){ return e.roleId === role.id && !e.vacant && !e._builtStale; })); });
      hcStaff = Math.round(hcStaff*100)/100;
      var hcUnstaffed = hcRoles.length > 0 && hcStaff === 0;
      var hcRatioActual = hcStaff > 0 ? Math.round(orgHC / hcStaff) : 0;
      var hcAlert = hcUnstaffed || hcRatioActual > Math.round(benchmark * 1.25);
      var hcWarn  = !hcAlert && hcRatioActual > benchmark;

      // Requisition lane (e.g. recruiting) — load vs annual capacity
      var reqRoles = deptRoles.filter(function(r){ return r.supportMeta && r.supportMeta.driver === 'requisition'; });
      var reqAlert = false, reqWarn = false, reqUtilPct = 0, reqUnstaffed = false;
      if (reqRoles.length) {
        var totalReqCap = 0;
        reqRoles.forEach(function(role){ var f = fteWeightedCount(S.team.filter(function(e){ return e.roleId === role.id && !e.vacant && !e._builtStale; })); totalReqCap += f * (role.supportMeta.reqCapacity || 35); });
        var liveOpenReqs = S.team.filter(isVacancyRecruitable).length;
        reqUnstaffed = totalReqCap === 0;
        reqUtilPct = totalReqCap > 0 ? Math.round((liveOpenReqs / totalReqCap) * 100) : (liveOpenReqs > 0 ? 999 : 0);
        reqAlert = reqUnstaffed ? liveOpenReqs > 0 : reqUtilPct > 95;
        reqWarn = !reqAlert && !reqUnstaffed && reqUtilPct > 80;
      }

      // Volume/step-function lane (e.g. payroll) — org headcount vs seat ceiling
      var volRoles = deptRoles.filter(function(r){ return r.supportMeta && r.supportMeta.driver === 'volume'; });
      var volAlert = false, volWarn = false, volLoadPct = 0, volUnstaffed = false;
      if (volRoles.length) {
        var totalVolCeiling = 0;
        volRoles.forEach(function(role){ var f = fteWeightedCount(S.team.filter(function(e){ return e.roleId === role.id && !e.vacant && !e._builtStale; })); totalVolCeiling += f * (role.supportMeta.hcCeiling || 300); });
        volUnstaffed = totalVolCeiling === 0;
        volLoadPct = totalVolCeiling > 0 ? Math.round((orgHC / totalVolCeiling) * 100) : 999;
        volAlert = volUnstaffed || volLoadPct > 95;
        volWarn = !volAlert && volLoadPct > 80;
      }
      // Complexity lane is intentionally unthresholded — ratio/ceiling benchmarks don't apply to it by design.

      var anyUnstaffed = hcUnstaffed || reqUnstaffed || volUnstaffed;
      var flagged = hcAlert || reqAlert || volAlert;
      var watch = !flagged && (hcWarn || reqWarn || volWarn);
      return applyManagerEscalation(dep, { kind:'support', flagged: flagged, watch: watch, hcRatioActual: hcRatioActual, benchmark: benchmark,
        hcStaff: hcStaff, // headcount-exposed subset only — any seat-gap math derived from this ratio must use this count, not the full department roster
        reqUtilPct: reqUtilPct, reqAlert: reqAlert, volLoadPct: volLoadPct, volAlert: volAlert, anyUnstaffed: anyUnstaffed,
        lanesOverThreshold: [hcAlert&&'headcount', reqAlert&&'requisition', volAlert&&'volume'].filter(Boolean),
        unstaffedLanes: [hcUnstaffed&&'headcount', reqUnstaffed&&'requisition', volUnstaffed&&'volume'].filter(Boolean) });
    }
    // Pure judgment — no numeric formula by design; Manager Insight is the only signal it can carry.
    return applyManagerEscalation(dep, { kind:'judgment', flagged: false, watch: false });
  }

  // Maps an already-computed signal onto a single 0-100 severity score so departments measured in
  // completely different units (FTE gap, coverage blocks, a support ratio, a manager escalation)
  // can share one red/yellow/green scale. Data Invalid and Not Configured deliberately return
  // null — "we can't tell you anything" is not a point on a severity scale, and placing one there
  // would misrepresent an unknown as a known-bad, which is exactly the distinction the Data
  // Invalid gate exists to protect elsewhere in this model.
  function computeDeptSeverity(dep, sig) {
    if (!sig || sig.dataInvalid || sig.notConfigured) return null;
    var HEALTHY = 5;
    function watchBand(frac) { return 35 + 30 * Math.max(0, Math.min(1, frac)); }  // 35–65
    function flagBand(frac)  { return 70 + 30 * Math.max(0, Math.min(1, frac)); }  // 70–100

    if (sig.kind === 'volume') {
      var trig = (typeof dep.fteTrig === 'number') ? dep.fteTrig : 0.5;
      if (!trig) return sig.flagged ? 100 : sig.watch ? 50 : HEALTHY;
      if (sig.flagged) return flagBand((sig.volGap / trig) - 1);
      if (sig.watch) return watchBand(sig.volGap / trig);
      return HEALTHY;
    }
    if (sig.kind === 'support') {
      // Each lane's own load relative to ITS OWN alert line (hc: 1.25x benchmark; req/vol: 95%) —
      // "1.0" means exactly at that lane's alert threshold. Uses whichever lane is worst, matching
      // the same "flags if any lane alerts" logic the boolean status already uses.
      var ratios = [];
      if (sig.hcRatioActual != null && sig.benchmark) ratios.push(sig.hcRatioActual / (sig.benchmark * 1.25));
      if (sig.reqUtilPct != null) ratios.push(sig.reqUtilPct / 95);
      if (sig.volLoadPct != null) ratios.push(sig.volLoadPct / 95);
      var worst = ratios.length ? Math.max.apply(null, ratios) : 0;
      if (sig.flagged) return flagBand(worst - 1);
      if (sig.watch) return watchBand(worst);
      return HEALTHY;
    }
    if (sig.kind === 'coverage') {
      if (sig.allSeatsVacant) return 100;
      if (sig.flagged) return flagBand((sig.covUncovered - 1) / 3);
      if (sig.watch) return watchBand(sig.covSingle / 4);
      return HEALTHY;
    }
    if (sig.kind === 'judgment') {
      if (sig.flagged) return 85; // manager escalation is binary — no natural magnitude to scale by
      if (sig.watch) return 45;
      return HEALTHY;
    }
    return null;
  }

  // Weekly Impact — translates the abstract FTE gap into hours/week, which is what a manager
  // actually feels, and pairs it with a real trend once enough history exists. Deliberately scoped
  // to volume-threshold departments only, and only their FLAGGED gap — coverage/support/judgment
  // don't have an FTE gap to convert, and a Watch-level gap hasn't crossed into "actionable
  // shortage" yet (shown separately, smaller, as at-risk context rather than folded into the same
  // headline number).
  // A department counts toward org-wide reporting (Executive Summary, Weekly Impact, Hiring
  // Alerts totals, exports) only once its methodology has passed the Validation Gate. A reviewer
  // correctly caught that every department's numbers were being blended into the executive view
  // regardless of whether anyone had validated that department's methodology yet — meaning an
  // unvalidated department's Flagged status could silently inflate a number presented as an
  // organizational finding. Default is NOT validated; a department must be explicitly marked.
  function isApprovedForReporting(dep) {
    return !!(dep && dep.approvedForReporting);
  }

  function computeWeeklyImpact() {
    var signals = S.depts.map(function(dep) { return { dep: dep, sig: computeDeptSignal(dep) }; });
    var approvedSignals = signals.filter(function(x) { return isApprovedForReporting(x.dep); });
    var flaggedVol = approvedSignals.filter(function(x) { return x.sig.kind === 'volume' && x.sig.flagged; });
    var watchVol = approvedSignals.filter(function(x) { return x.sig.kind === 'volume' && x.sig.watch; });
    var hrsShort = Math.round(flaggedVol.reduce(function(s, x) { return s + Math.max(0, x.sig.volGap); }, 0) * 40);
    var hrsAtRisk = Math.round(watchVol.reduce(function(s, x) { return s + Math.max(0, x.sig.volGap); }, 0) * 40);
    var unapprovedCount = signals.length - approvedSignals.length;
    var unapprovedFlaggedCount = signals.filter(function(x) { return !isApprovedForReporting(x.dep) && x.sig.flagged; }).length;

    // Trend: sum each APPROVED volume department's OWN volGap from a snapshot genuinely >=7 days
    // old, converted to hours the same way. Requires every one of today's flagged/watch approved
    // departments to have real history — if any is missing it, the honest answer is "insufficient
    // history", not a number that quietly assumes zero for the department we don't have data on.
    var todayStr = new Date().toISOString().slice(0, 10);
    var target = new Date(); target.setDate(target.getDate() - 7);
    var targetStr = target.toISOString().slice(0, 10);
    var volDepts = approvedSignals.filter(function(x) { return x.sig.kind === 'volume'; });
    var pastHrsShort = 0, missing = false, foundAny = false;
    volDepts.forEach(function(x) {
      var hist = (S.statusHistory && S.statusHistory[x.dep.id]) || [];
      var ref = null;
      for (var i = 0; i < hist.length; i++) { if (hist[i].date <= targetStr) ref = hist[i]; }
      if (!ref || ref.volGap == null) { missing = true; return; }
      foundAny = true;
      pastHrsShort += Math.max(0, ref.volGap) * 40;
    });
    pastHrsShort = Math.round(pastHrsShort);
    var trend = null;
    if (!missing && foundAny) {
      var delta = hrsShort - pastHrsShort;
      trend = Math.abs(delta) < 8 ? { direction: 'flat', delta: delta } : { direction: delta > 0 ? 'worsening' : 'improving', delta: delta };
    }

    return { hrsShort: hrsShort, hrsAtRisk: hrsAtRisk, flaggedCount: flaggedVol.length, watchCount: watchVol.length, trend: trend, hasVolumeDepts: volDepts.length > 0,
      unapprovedCount: unapprovedCount, unapprovedFlaggedCount: unapprovedFlaggedCount, totalDeptCount: signals.length };
  }

  var BUILT_IMP = { raw:[], headers:[], mapping:{}, fileName:'' };

  // Fields we try to detect in the Built export
  var BUILT_IMP_FIELDS = [
    { key:'posTitle',   label:'Position Title',    required:true,  hint:'Approved role/position name' },
    { key:'dept',       label:'Department',         required:true,  hint:'Department or cost center' },
    { key:'positionId', label:'Position ID',        required:false, hint:'Built\'s unique position/req number (e.g. PCN). Strongly recommended — without it, positions that share a title and department cannot be told apart and will each be imported as a separate register row rather than consolidated.' },
    { key:'incumbent',  label:'Incumbent Name',     required:false, hint:'Current employee in seat — blank = vacant' },
    { key:'posStatus',  label:'Position Status',    required:false, hint:'Active, Frozen, Pending Approval' },
    { key:'empStatus',  label:'Employee Status',    required:false, hint:'Active, On Leave, Terminated' },
    { key:'funding',    label:'Funding Source',     required:false, hint:'Operating, Capital, Grant...' },
    { key:'budgetCode', label:'Budget / GL Code',   required:false, hint:'Cost center or GL account' },
    { key:'annualBudget',label:'Annual Budget ($)', required:false, hint:'Loaded cost per seat per year' },
    { key:'seats',      label:'Approved Seats',     required:false, hint:'FTE count approved. Defaults to 1.' },
    { key:'empType',    label:'Employment Type',    required:false, hint:'Full-time, Part-time, Contractor' },
    { key:'wkHrs',      label:'Weekly Hours / FTE', required:false, hint:'Optional — real hrs/wk if Built has it. If mapped, this replaces the Employment-Type-based 20/40hr estimate entirely.' },
    { key:'hireDate',   label:'Hire / Start Date',  required:false, hint:'For reference' },
  ];

  var BUILT_IMP_PATTERNS = {
    posTitle:    [/position.?title/i,/pos.?title/i,/job.?title/i,/role/i,/position/i,/title/i],
    dept:        [/dept/i,/department/i,/division/i,/cost.?center/i,/org.?unit/i,/team/i],
    positionId:  [/position.?id/i,/pos.?id/i,/position.?number/i,/pos.?num/i,/pcn/i,/req.?id/i,/req.?number/i,/requisition/i],
    incumbent:   [/incumbent/i,/employee/i,/name/i,/person/i,/occupant/i,/worker/i,/filled.?by/i],
    posStatus:   [/pos.?status/i,/position.?status/i,/req.?status/i,/status/i],
    empStatus:   [/emp.?status/i,/employee.?status/i,/worker.?status/i,/active/i],
    funding:     [/fund/i,/source/i,/fund.?type/i,/pay.?type/i],
    budgetCode:  [/budget.?code/i,/gl/i,/cost.?code/i,/account/i,/code/i],
    annualBudget:[/annual.?budget/i,/loaded.?cost/i,/salary/i,/comp/i,/pay.?rate/i,/total.?comp/i],
    seats:       [/seat/i,/count/i,/headcount/i,/qty/i,/approved/i],
    empType:     [/emp.?type/i,/employment.?type/i,/worker.?type/i,/category/i,/type/i],
    // Deliberately does NOT include a bare /fte/i pattern. Many real HRIS exports have a column
    // literally titled "FTE" holding a fraction (0.5, 0.8, 1.0) — auto-mapping that to weekly
    // hours would feed those fractions directly into the hours field as-is (0.8 "hours/week"
    // instead of 32), silently wiping out productive FTE for anyone affected. Better to require
    // manual mapping for a bare "FTE" column than to guess wrong on something this consequential
    // — see also the sanity-range check on parsed values in parseBuiltRows below.
    wkHrs:       [/wk.?hrs/i,/weekly.?hours/i,/hours.?per.?week/i,/hours.?wk/i,/hrs.?wk/i,/wk.?hours/i,/fte.?hours/i,/hours.*fte/i],
    hireDate:    [/hire.?date/i,/start.?date/i,/join.?date/i,/date/i],
  };

  // ── Built import: match-and-refresh, not replace ─────────────────────────────
  // A re-import must not rebuild every employee record with a new id — that discards manager-owned
  // enrichment (seniority, departure risk, ramp status, anything else recorded outside Built) and
  // orphans anything that references an employee by id (outage records, Time Audit data). Instead,
  // each row in the new import is matched against the EXISTING Built-sourced roster, preferring the
  // Position ID (the only reliably stable key) and falling back to an unambiguous name+dept+role
  // match when Position ID isn't available. A match updates the existing record in place — same id,
  // only the Built-owned fields refreshed. No match means a genuinely new record. An existing
  // Built-sourced employee untouched by the new import is flagged, never silently deleted, since
  // deleting it would be exactly the same kind of silent data loss this exists to prevent.
  function normalizeMatchKey(str) { return String(str||'').trim().toLowerCase().replace(/\s+/g,' '); }

  // Pure planning pass — reads S.team/S.headcountPlan but does not mutate them, so it can be run
  // once to show a confirmation preview and again (or reused) to actually commit. Returns the full
  // merge plan: which target seats matched an existing employee (and which), which are new, which
  // existing Built-sourced employees go unmatched (stale), and the position-register plan.
  function planBuiltImportMerge(okRows) {
    var targetSeats = [];
    okRows.forEach(function(r) {
      var roleId = r.matchedRoleId, deptId = r.matchedDeptId;
      var seats = r.seats || 1;
      var isPartTime = /part.?time/i.test(r.empType || '');
      var hasRealHrs = r.wkHrs != null && !isNaN(r.wkHrs) && r.wkHrs > 0;
      var defaultHrs = hasRealHrs ? r.wkHrs : (isPartTime ? 20 : 40);
      if (!r.isVacant) {
        targetSeats.push({
          filled: true, builtPosId: r.builtPosId || null, name: r.incumbent, dept: deptId, roleId: roleId,
          hrs: defaultHrs, hasRealHrs: hasRealHrs, isPartTimeEstimate: isPartTime && !hasRealHrs,
          _emptype: r.empType, isOnLeave: r.isOnLeave, _builtBudgetCode: r.budgetCode,
        });
        seats -= 1;
      }
      for (var vi = 0; vi < Math.max(0, seats); vi++) {
        targetSeats.push({
          filled: false, builtPosId: r.builtPosId || null, dept: deptId, roleId: roleId,
          _builtStatus: r.posStatus, _builtFunding: r.funding, _builtBudgetCode: r.budgetCode,
        });
      }
    });

    // Two separate candidate pools, deliberately scoped differently:
    //  - existingByPosId draws from ALL of S.team (not just previously Built-sourced records) —
    //    a manually-seeded or hand-entered employee is a perfectly valid match target the first
    //    time a real Built row turns out to describe them. Restricting this to _source==='built'
    //    was the bug: on a first-ever import, none of the preloaded/manually-entered roster
    //    carries that flag, so nothing could ever match against it and every one of those people
    //    got a silent duplicate record instead of being recognized. In practice only records that
    //    went through a prior Built import ever have _builtPosId set at all, so this widening is
    //    safe — it can't accidentally match on a field nobody else populates.
    //  - existingByNameKey is likewise drawn from all non-vacant employees, same reasoning.
    var existingByPosId = {};
    S.team.forEach(function(e) {
      if (e._builtPosId) { (existingByPosId[e._builtPosId] = existingByPosId[e._builtPosId] || []).push(e); }
    });
    var existingByNameKey = {};
    S.team.forEach(function(e) {
      if (e.vacant || e._builtStale) return;
      var key = normalizeMatchKey(e.name) + '|' + e.dept + '|' + e.roleId;
      (existingByNameKey[key] = existingByNameKey[key] || []).push(e);
    });
    // staleEmployees, below, still only ever draws from PREVIOUSLY Built-sourced records — a
    // manually-entered employee who simply isn't mentioned in this particular import was never
    // Built-sourced to begin with, so "no longer in Built" doesn't apply to them; they're left
    // alone entirely unless a row actually matches them.
    var existingBuilt = S.team.filter(function(e) { return e._source === 'built'; });
    var usedExistingIds = {};

    targetSeats.forEach(function(seat) {
      var matched = null;
      if (seat.builtPosId && existingByPosId[seat.builtPosId] && existingByPosId[seat.builtPosId].length) {
        var pool = existingByPosId[seat.builtPosId];
        var sameStatusIdx = pool.findIndex(function(e) { return !!e.vacant === !seat.filled; });
        var idx = sameStatusIdx !== -1 ? sameStatusIdx : 0;
        var candidate = pool[idx];
        // Position ID proves it's the same SEAT, not the same PERSON. If the seat was previously
        // filled by a real, named person and this row either leaves it vacant or names someone
        // different, that's a personnel change, not a continuing record — reusing the old record
        // in place would silently carry the departed person's seniority, departure risk, and any
        // outage/output history over onto a different (or absent) person. Treat it explicitly as
        // a departure instead: the old record is left unconsumed here (so it falls through to
        // staleEmployees below, flagged and preserved, never deleted) and a genuinely fresh
        // record gets created for whoever/whatever is in the seat now.
        var isTurnover = candidate && !candidate.vacant && candidate.name &&
          (!seat.filled || normalizeMatchKey(candidate.name) !== normalizeMatchKey(seat.name));
        if (!isTurnover) {
          matched = pool.splice(idx, 1)[0];
        }
      }
      if (!matched && seat.filled) {
        var key = normalizeMatchKey(seat.name) + '|' + seat.dept + '|' + seat.roleId;
        var candidates = (existingByNameKey[key] || []).filter(function(e) { return !usedExistingIds[e.id]; });
        if (candidates.length === 1) matched = candidates[0];
      }
      if (matched) usedExistingIds[matched.id] = true;
      seat.matched = matched;
    });

    var staleEmployees = existingBuilt.filter(function(e) { return !usedExistingIds[e.id]; });

    // Position register plan — matched by Position ID only (role+dept is not a safe grouping key,
    // see the note in the commit step below); positions no longer present in the new import are
    // dropped rather than flagged, since nothing else holds a stable reference to a plan row's id
    // and an un-refreshed position would otherwise sit in the register indefinitely claiming a
    // budgeted seat Built no longer lists.
    var existingPlanByPosId = {};
    (S.headcountPlan || []).forEach(function(p) { if (p.builtPosId) existingPlanByPosId[p.builtPosId] = p; });
    var planMatchedCount = 0, planNewCount = 0;
    var seenPlanKeys = {};
    okRows.forEach(function(r, ri) {
      var key = r.builtPosId ? ('pid:' + r.builtPosId) : ('row:' + ri);
      if (seenPlanKeys[key]) return;
      seenPlanKeys[key] = true;
      if (r.builtPosId && existingPlanByPosId[r.builtPosId]) planMatchedCount++; else planNewCount++;
    });
    var planStaleCount = (S.headcountPlan || []).filter(function(p) { return p.builtPosId && !okRows.some(function(r) { return r.builtPosId === p.builtPosId; }); }).length;

    return {
      targetSeats: targetSeats,
      staleEmployees: staleEmployees,
      matchedCount: targetSeats.filter(function(s) { return s.matched; }).length,
      newCount: targetSeats.filter(function(s) { return !s.matched; }).length,
      staleCount: staleEmployees.length,
      isReimport: existingBuilt.length > 0,
      planMatchedCount: planMatchedCount, planNewCount: planNewCount, planStaleCount: planStaleCount,
    };
  }

  // Commits a plan produced by planBuiltImportMerge — the only place S.team / S.headcountPlan
  // actually get mutated for a Built import.
  function commitBuiltImportMerge(plan, okRows) {
    var mergedTeam = [];
    plan.targetSeats.forEach(function(seat) {
      var matched = seat.matched;
      if (matched) {
        matched.dept = seat.dept;
        matched.roleId = seat.roleId;
        matched._builtPosId = seat.builtPosId;
        matched._builtBudgetCode = seat._builtBudgetCode;
        matched._source = 'built';
        matched._builtStale = false;
        matched._builtStaleSince = undefined;
        if (seat.filled) {
          matched.vacant = false;
          matched.name = seat.name;
          matched._emptype = seat._emptype;
          matched._onLeaveFlag = seat.isOnLeave;
          // Only refresh hours if Built now provides a real value, or the existing hours were
          // still an unreviewed estimate — never silently overwrite an hours value a manager has
          // confirmed (S.team[i].hrs edits in Team Roster explicitly clear _hrsEstimated).
          if (seat.hasRealHrs || matched._hrsEstimated !== false) {
            matched.hrs = seat.hrs;
            matched._hrsEstimated = !seat.hasRealHrs;
          }
        } else {
          matched.vacant = true;
          matched.name = 'Vacant';
          matched._builtStatus = seat._builtStatus;
          matched._builtFunding = seat._builtFunding;
          matched._hireEligible = isHireEligible({ status: seat._builtStatus, funding: seat._builtFunding });
        }
        mergedTeam.push(matched);
      } else if (seat.filled) {
        mergedTeam.push({
          id: 'e-built-' + Date.now() + Math.random().toString(36).slice(2,5),
          name: seat.name, dept: seat.dept, roleId: seat.roleId, hrs: seat.hrs,
          _hrsEstimated: !seat.hasRealHrs, _emptype: seat._emptype, _onLeaveFlag: seat.isOnLeave,
          _source: 'built', _builtBudgetCode: seat._builtBudgetCode, _builtPosId: seat.builtPosId,
        });
      } else {
        mergedTeam.push({
          id: 'vac-built-' + Date.now() + Math.random().toString(36).slice(2,6) + Math.random().toString(36).slice(2,4),
          name: 'Vacant', dept: seat.dept, roleId: seat.roleId, hrs: 40, vacant: true,
          _builtStatus: seat._builtStatus, _builtFunding: seat._builtFunding, _builtBudgetCode: seat._builtBudgetCode,
          _hireEligible: isHireEligible({ status: seat._builtStatus, funding: seat._builtFunding }),
          _source: 'built', _builtPosId: seat.builtPosId,
        });
      }
    });

    plan.staleEmployees.forEach(function(e) { e._builtStale = true; e._builtStaleSince = new Date().toLocaleString(); });

    var manualTeam = S.team.filter(function(e) { return e._source !== 'built'; });
    S.team = manualTeam.concat(mergedTeam).concat(plan.staleEmployees);

    // ── Position register — same match-by-Position-ID-and-refresh-in-place principle ──────────
    var existingPlanByPosId = {};
    (S.headcountPlan || []).forEach(function(p) { if (p.builtPosId) existingPlanByPosId[p.builtPosId] = p; });
    var planMap = {};
    okRows.forEach(function(r, ri) {
      var key = r.builtPosId ? ('pid:' + r.builtPosId) : ('row:' + ri);
      if (!planMap[key]) {
        var existing = r.builtPosId ? existingPlanByPosId[r.builtPosId] : null;
        planMap[key] = existing || { id: 'hp-built-' + Date.now() + Math.random().toString(36).slice(2,5), builtPosId: r.builtPosId || null };
        planMap[key].roleId = r.matchedRoleId;
        planMap[key].dept = r.matchedDeptId;
        planMap[key].count = 0;
        planMap[key].funding = r.funding;
        planMap[key].status = r.posStatus;
        planMap[key].budgetCode = r.budgetCode;
        planMap[key].annualBudget = r.annualBudget;
        planMap[key].note = r.posTitle + (BUILT_IMP.fileName ? ' · Built: ' + BUILT_IMP.fileName : '');
        planMap[key]._filledCount = 0;
      }
      planMap[key].count += r.seats;
      if (!r.isVacant) planMap[key]._filledCount++;
      if (r.annualBudget > planMap[key].annualBudget) planMap[key].annualBudget = r.annualBudget;
    });
    S.headcountPlan = Object.values(planMap);
  }

  function isHireEligible(plan){
    // A vacancy is only eligible if position is active (not frozen/pending/unknown) AND funded
    // (not frozen/unknown). An unrecognized or blank value from Built is treated as unverified,
    // not as a green light — Built is the source of truth, so silence from it should never read
    // as permission to hire.
    return (plan.status==='active') && (plan.funding !== 'frozen') && (plan.funding !== 'unknown');
  }

  // A team-roster vacancy is "recruitable" unless it's currently ineligible. Eligibility is
  // resolved LIVE from the SPECIFIC position row this vacancy is linked to (via _planId, the
  // position row's own stable id — present on every row regardless of source: Built import,
  // preloaded seed data, or a manually-added row) — never from a snapshot frozen at import or
  // creation time. Editing a position's status in Configure must take effect everywhere
  // immediately. _planId is checked before the legacy _builtPosId link (kept for records created
  // before _planId existed) — a position row's own id is universal; builtPosId is null for any
  // row that didn't come from a Built file, which is exactly the case that broke this before.
  // Vacancies with no resolvable link at all fall back to their own _hireEligible flag, defaulting
  // to recruitable absent evidence otherwise — a missing flag should never read as "not
  // recruitable" and wrongly zero out a real, legitimate open seat.
  // Single definition for "counts as a real, present roster record" — every live calculation
  // should ultimately agree with this rather than re-deriving its own notion of active/stale.
  function isActiveRosterRecord(e) { return !e._builtStale; }

  function isVacancyRecruitable(e){
    if (!e.vacant || e._builtStale) return false;
    if (e._planId) {
      var linkedPlan = (S.headcountPlan || []).find(function(p) { return p.id === e._planId; });
      if (linkedPlan) return isHireEligible(linkedPlan);
    }
    if (e._builtPosId) {
      var linkedByBuiltId = (S.headcountPlan || []).find(function(p) { return p.builtPosId === e._builtPosId; });
      if (linkedByBuiltId) return isHireEligible(linkedByBuiltId);
    }
    return e._hireEligible !== false;
  }

  // Runs the position-specific reconciliation for every role+dept pool that has at least one
  // position row — called once on page load (and again after loading a saved/older config file)
  // so a legacy or sample vacancy that predates position-specific linking gets attributed to its
  // correct row immediately, rather than sitting unlinked until the first time someone happens to
  // edit that row. This is what makes a freshly-opened file (or a freshly-loaded older save) show
  // Open Reqs and Hire-Eligible Vacancies already in agreement, with no edit required first.
  function syncAllVacancyRecords() {
    var seen = {};
    (S.headcountPlan || []).forEach(function(p) {
      var key = p.roleId + '|' + p.dept;
      if (seen[key]) return;
      seen[key] = true;
      syncVacancyRecordsForRoleDept(p.roleId, p.dept);
    });
  }

  // Keeps roster vacancy placeholders in sync with EVERY position row sharing this role+dept,
  // reconciled individually per row — never pooled. Two rows can legitimately share a role+dept
  // with completely different eligibility (an Active row and a separate Pending Approval row for
  // the same role is a normal, real Built pattern — Settlement has exactly this), so a vacancy
  // must be attributed to the SPECIFIC row it represents, not "whichever row happens to be listed
  // first." Only ever adds or removes VACANT placeholders — never touches a filled seat. Running
  // this twice in a row on unchanged input is a no-op (see the self-check's idempotency test) —
  // safe to call defensively, e.g. from syncAllVacancyRecords on every page load.
  function syncVacancyRecordsForRoleDept(roleId, deptId) {
    var relevantPlans = (S.headcountPlan || []).filter(function(p) { return p.roleId === roleId && p.dept === deptId; });
    var alloc = getPlanAllocations();

    // Split the current vacant pool into "already linked to one of these specific rows" and
    // "unlinked" (seed data, or anything created before per-position linking existed).
    var linkedByPlanId = {};
    relevantPlans.forEach(function(p) { linkedByPlanId[p.id] = []; });
    var unlinked = [];
    S.team.forEach(function(e) {
      if (!(e.roleId === roleId && e.dept === deptId && e.vacant && !e._builtStale)) return;
      if (e._planId && linkedByPlanId[e._planId]) linkedByPlanId[e._planId].push(e);
      else unlinked.push(e);
    });

    // Attribute unlinked records to whichever row still needs a vacancy, hire-eligible rows
    // first — an unlinked vacancy with no other information is more likely to represent a real,
    // currently-recruitable seat than a not-yet-approved one. Deterministic tie-break by plan id
    // so this is reproducible, not order-of-iteration-dependent.
    var needyPlansFirst = relevantPlans.slice().sort(function(a, b) {
      var ae = isHireEligible(a) ? 0 : 1, be = isHireEligible(b) ? 0 : 1;
      if (ae !== be) return ae - be;
      return a.id < b.id ? -1 : 1;
    });
    needyPlansFirst.forEach(function(plan) {
      var needed = alloc[plan.id] ? alloc[plan.id].vacant : 0;
      while (linkedByPlanId[plan.id].length < needed && unlinked.length) {
        var e = unlinked.shift();
        e._planId = plan.id;
        e._builtPosId = plan.builtPosId || e._builtPosId || null;
        e._builtStatus = plan.status; e._builtFunding = plan.funding;
        linkedByPlanId[plan.id].push(e);
      }
    });

    // Reconcile each row individually against its own need: add a new, correctly-linked
    // placeholder for a real shortfall, or remove surplus that belongs SPECIFICALLY to that row.
    relevantPlans.forEach(function(plan) {
      var needed = alloc[plan.id] ? alloc[plan.id].vacant : 0;
      var have = linkedByPlanId[plan.id];
      if (have.length < needed) {
        var toAdd = needed - have.length;
        for (var i = 0; i < toAdd; i++) {
          S.team.push({
            id: 'vac-sync-' + Date.now() + Math.random().toString(36).slice(2, 6) + '-' + i,
            name: 'Vacant', dept: deptId, roleId: roleId, hrs: 40, vacant: true,
            _source: 'built', _planId: plan.id, _builtPosId: plan.builtPosId || null,
            _builtStatus: plan.status, _builtFunding: plan.funding,
            _hireEligible: isHireEligible(plan),
          });
        }
      } else if (have.length > needed) {
        var toRemove = have.length - needed;
        for (var j = 0; j < toRemove; j++) {
          var idx = S.team.indexOf(have[j]);
          if (idx !== -1) S.team.splice(idx, 1);
        }
      }
    });

    // Anything still unlinked after attribution wasn't needed by any row sharing this role+dept
    // — surplus, remove it. (No person or history is ever lost here: these are placeholder
    // vacancy records, never filled seats.)
    unlinked.forEach(function(e) {
      var idx = S.team.indexOf(e);
      if (idx !== -1) S.team.splice(idx, 1);
    });
  }

  // Single source of truth for filled/vacant allocation across position-plan rows. Multiple rows
  // can share the same role+dept (an active position plus a separate forecasted seat) — each real
  // incumbent can only fill one row's seat. This partitions the real incumbent pool across rows in
  // a stable order and returns one allocation per plan row. Every consumer (screen, KPI, CSV export,
  // Business Case, Decision page) must use this instead of independently re-counting incumbents.
  function getPlanAllocations() {
    var sorted = (S.headcountPlan||[]).slice().sort(function(a,b){ return a.id < b.id ? -1 : 1; });
    var claimedByKey = {}, totalIncumbentsByKey = {};
    var byId = {};
    sorted.forEach(function(plan){
      var key = plan.roleId+'|'+plan.dept;
      if (totalIncumbentsByKey[key] === undefined) {
        totalIncumbentsByKey[key] = S.team.filter(function(e){ return e.roleId===plan.roleId && e.dept===plan.dept && !e.vacant && !e._builtStale; }).length;
        claimedByKey[key] = 0;
      }
      var availableForThisRow = totalIncumbentsByKey[key] - claimedByKey[key];
      var filled = Math.max(0, Math.min(plan.count, availableForThisRow));
      claimedByKey[key] += filled;
      var vacant = plan.count - filled;
      var eligible = isHireEligible(plan);
      byId[plan.id] = { approved: plan.count, filled: filled, vacant: vacant, hireEligible: eligible && vacant > 0, blocked: !eligible && vacant > 0 };
    });
    return byId;
  }

  // Live-engine snapshot for Scenario Compare. Reuses computeDeptSeverity (already shared with
  // Executive Summary's trend) as the one comparable figure across department types that are
  // otherwise measured in completely different units.
  function _captureLiveMetrics() {
    var snap = {};
    S.depts.forEach(function(d) {
      var sig = computeDeptSignal(d);
      snap[d.id] = {
        name: d.name, kind: sig.kind,
        flagged: !!sig.flagged, watch: !!sig.watch, dataInvalid: !!sig.dataInvalid, notConfigured: !!sig.notConfigured,
        severity: computeDeptSeverity(d, sig),
        statusLabel: sig.dataInvalid ? 'Data Invalid' : sig.notConfigured ? 'Not Configured' : sig.flagged ? 'Flagged' : sig.watch ? 'Watch' : 'Healthy',
      };
    });
    return snap;
  }

  // Every fixture and expected value below was hand-verified against this exact production code
  // path before being hardcoded here (not derived from it after the fact) — the point is to catch
  // a FUTURE change that silently alters behavior, so the expected values must be independent of
  // whatever the code currently does. Fixtures are self-contained (never read from the live S), so
  // editing your real config can never change what this check expects or reports.
  //
  // This calls the real computeDeptSignal / applyManagerEscalation / isHireEligible /
  // isVacancyRecruitable / computeBizCaseVerdict functions directly — it does not reimplement the
  // model's logic, so it can't pass by agreeing with a bug. To do that safely it has to temporarily
  // swap the global S for each fixture; that swap and restore happens synchronously with no DOM
  // rendering in between, so the live tool's on-screen data is never touched or visible mid-check.
  function runModelSelfCheck() {
    var results = [];
    function record(group, desc, pass, detail) {
      results.push({ group: group, desc: desc, pass: !!pass, detail: detail || '' });
    }
    function assertField(group, desc, sig, field, expected) {
      var actual = sig ? sig[field] : undefined;
      var pass;
      if (typeof expected === 'number') {
        pass = typeof actual === 'number' && Math.abs(actual - expected) < 0.005;
      } else {
        pass = actual === expected;
      }
      record(group, desc + ' — ' + field, pass, 'expected ' + JSON.stringify(expected) + ', got ' + JSON.stringify(actual));
    }

    var _savedS = S;
    try {

      // ── 1 & 2. Settlement's worked example + MAX/SUM boundary ─────────────
      // Real shipped Settlement (d5) numbers: two volume drivers (3000/FTE @ 17,800 current;
      // 220/FTE @ 940 current), fteTrig .5, six filled seats (one still ramping at 60% productive)
      // plus one approved-but-vacant seat. This is deliberately also the MAX/SUM boundary case: the
      // two combine modes disagree not just numerically but on FLAGGED vs WATCH for this exact data.
      (function() {
        S = {
          settings: { orgHeadcount: 335, hrBenchmarkRatio: 60 }, managerSignals: {}, deptVolumes: {},
          // Real shipped haircut defaults (Configure → Haircuts) — a reviewer correctly caught that
          // this fixture previously omitted S.haircut entirely, so it silently tested a zero-haircut
          // scenario that no longer matches what a real user sees. The worked example must use the
          // same baseline as the shipped configuration, not a more convenient one.
          haircut: { pto: 15, hol: 10, sick: 5, interrupt: 8 },
          roles: [
            { id:'r21', dept:'d5', name:'Settlement Analyst', hrs:40, rampProd:.60 },
            { id:'r22', dept:'d5', name:'Sr. Settlement Analyst', hrs:40, rampProd:.55 },
            { id:'r23', dept:'d5', name:'Settlement Specialist', hrs:40, rampProd:.55 },
            { id:'r24', dept:'d5', name:'Settlement Manager', hrs:40, rampProd:.50 },
          ],
          team: [
            { id:'e26', name:'Settlement Analyst 1', dept:'d5', roleId:'r21', hrs:40, vacant:false, rampWeeksRemaining:6 },
            { id:'e27', name:'Settlement Analyst 2', dept:'d5', roleId:'r21', hrs:40, vacant:false },
            { id:'e28', name:'Settlement Analyst 3', dept:'d5', roleId:'r21', hrs:40, vacant:false },
            { id:'e29', name:'Sr. Settlement Analyst', dept:'d5', roleId:'r22', hrs:40, vacant:false },
            { id:'e30', name:'Settlement Specialist', dept:'d5', roleId:'r23', hrs:40, vacant:false },
            { id:'e31', name:'Settlement Manager', dept:'d5', roleId:'r24', hrs:40, vacant:false },
            { id:'e33', name:'Vacant', dept:'d5', roleId:'r21', hrs:40, vacant:true },
          ],
        };
        var depBase = { id:'d5', name:'Settlement', type:'service', fteTrig:.5,
          volDrivers:[{name:'Trades settled / month',perFTE:3000,current:17800},{name:'Invoices processed / month',perFTE:220,current:940}] };

        var sigMax = computeDeptSignal(Object.assign({}, depBase));
        assertField('Settlement worked example (MAX, default)', 'Settlement, default MAX combine', sigMax, 'impliedFTE', 6);
        assertField('Settlement worked example (MAX, default)', 'Settlement, default MAX combine', sigMax, 'productiveFTE', 4.95);
        assertField('Settlement worked example (MAX, default)', 'Settlement, default MAX combine', sigMax, 'volGap', 1.05);
        assertField('Settlement worked example (MAX, default)', 'Settlement, default MAX combine', sigMax, 'flagged', true);
        assertField('Settlement worked example (MAX, default)', 'Settlement, default MAX combine', sigMax, 'watch', false);

        var sigSum = computeDeptSignal(Object.assign({}, depBase, { volCombineMode:'sum' }));
        assertField('MAX/SUM boundary', 'Same data, SUM combine', sigSum, 'impliedFTE', 11);
        assertField('MAX/SUM boundary', 'Same data, SUM combine', sigSum, 'volGap', 6.05);
        assertField('MAX/SUM boundary', 'Same data, SUM combine', sigSum, 'flagged', true);
        assertField('MAX/SUM boundary', 'Same data, SUM combine', sigSum, 'watch', false);
        record('MAX/SUM boundary', 'MAX and SUM produce different gap magnitudes for identical inputs',
          sigMax.volGap !== sigSum.volGap, 'MAX volGap=' + sigMax.volGap + ', SUM volGap=' + sigSum.volGap);

        // A reviewer also caught that this same example, run with the haircut fields omitted
        // entirely (the old fixture shape), still silently passes — since that's now a distinct,
        // deliberately-tested scenario (zero time-off assumption) rather than an accident. Kept
        // as its own explicit case so the "no haircut configured" path stays covered too.
        var sigNoHaircutS = Object.assign({}, S, { haircut: undefined });
        var _savedHaircutHolder = S.haircut;
        S.haircut = undefined;
        var sigZeroHaircut = computeDeptSignal(depBase);
        assertField('Settlement worked example (MAX, default)', 'Same data, haircut explicitly unset', sigZeroHaircut, 'productiveFTE', 5.6);
        assertField('Settlement worked example (MAX, default)', 'Same data, haircut explicitly unset', sigZeroHaircut, 'volGap', 0.4);
        S.haircut = _savedHaircutHolder;
      })();

      // ── 3. Coverage transitions ────────────────────────────────────────────
      (function() {
        var __scCovGrid;
        function buildCoverageS(grid, teamCount, allVacant) {
          __scCovGrid = grid;
          var team = [];
          if (allVacant) {
            for (var i = 0; i < 3; i++) team.push({ id:'v'+i, name:'Vacant', dept:'d_sc_cov', roleId:'r_sc_trader', hrs:40, vacant:true });
          } else {
            for (var j = 0; j < teamCount; j++) team.push({ id:'e'+j, name:'Trader '+j, dept:'d_sc_cov', roleId:'r_sc_trader', hrs:40, vacant:false });
          }
          S = { settings:{orgHeadcount:335,hrBenchmarkRatio:60}, managerSignals:{}, deptVolumes:{},
            roles:[{id:'r_sc_trader', dept:'d_sc_cov', name:'Trader'}], team: team };
        }
        var dep = { id:'d_sc_cov', name:'Self-Check Coverage Dept', type:'service' };
        var _origIsCovDept = COVERAGE_DEPT_IDS.indexOf('d_sc_cov');
        if (_origIsCovDept === -1) COVERAGE_DEPT_IDS.push('d_sc_cov');
        COVERAGE_DEPT_ROW['d_sc_cov'] = '__sc__';
        var _origGetTradeCov = getTradeCov;
        getTradeCov = function(row, col) { return row === '__sc__' ? __scCovGrid[col] : _origGetTradeCov(row, col); };

        buildCoverageS([2,2,2,2,2,2], 3, false);
        var sigHealthy = computeDeptSignal(dep);
        assertField('Coverage transitions', 'All blocks double-covered', sigHealthy, 'flagged', false);
        assertField('Coverage transitions', 'All blocks double-covered', sigHealthy, 'watch', false);
        assertField('Coverage transitions', 'All blocks double-covered', sigHealthy, 'covUncovered', 0);

        buildCoverageS([2,1,2,1,2,2], 3, false);
        var sigWatch = computeDeptSignal(dep);
        assertField('Coverage transitions', 'Single-point blocks, no true gaps', sigWatch, 'flagged', false);
        assertField('Coverage transitions', 'Single-point blocks, no true gaps', sigWatch, 'watch', true);
        assertField('Coverage transitions', 'Single-point blocks, no true gaps', sigWatch, 'covSingle', 2);

        buildCoverageS([0,2,2,1,2,2], 3, false);
        var sigFlag = computeDeptSignal(dep);
        assertField('Coverage transitions', 'At least one genuinely uncovered block', sigFlag, 'flagged', true);
        assertField('Coverage transitions', 'At least one genuinely uncovered block', sigFlag, 'covUncovered', 1);

        buildCoverageS([2,2,2,2,2,2], 0, true);
        var sigCrisis = computeDeptSignal(dep);
        assertField('Coverage transitions', 'Configured roster, every seat currently vacant', sigCrisis, 'flagged', true);
        assertField('Coverage transitions', 'Configured roster, every seat currently vacant', sigCrisis, 'allSeatsVacant', true);

        getTradeCov = _origGetTradeCov;
        if (_origIsCovDept === -1) COVERAGE_DEPT_IDS.pop();
        delete COVERAGE_DEPT_ROW['d_sc_cov'];
      })();

      // ── 4. Support-lane thresholds ──────────────────────────────────────────
      (function() {
        var depHC = { id:'d_sc_hr', name:'Self-Check Support Dept', type:'support', benchmark: null };
        function buildHC(staffCount) {
          var team = [];
          for (var i = 0; i < staffCount; i++) team.push({ id:'e'+i, name:'HRBP '+i, dept:'d_sc_hr', roleId:'r_sc_hc', hrs:40, vacant:false });
          S = { settings:{orgHeadcount:335,hrBenchmarkRatio:60}, managerSignals:{}, deptVolumes:{},
            roles:[{id:'r_sc_hc', dept:'d_sc_hr', name:'HR Business Partner', supportMeta:{driver:'headcount'}}], team: team };
        }
        buildHC(6);
        assertField('Support-lane thresholds', 'Headcount lane, ratio 56 vs benchmark 60', computeDeptSignal(depHC), 'flagged', false);
        buildHC(5);
        var hcWatchSig = computeDeptSignal(depHC);
        assertField('Support-lane thresholds', 'Headcount lane, ratio 67 (watch band)', hcWatchSig, 'flagged', false);
        assertField('Support-lane thresholds', 'Headcount lane, ratio 67 (watch band)', hcWatchSig, 'watch', true);
        buildHC(4);
        assertField('Support-lane thresholds', 'Headcount lane, ratio 84 (over 1.25x benchmark)', computeDeptSignal(depHC), 'flagged', true);

        function buildReq(liveOpenReqs, reqCapacity, recruiterCount) {
          var team = [];
          for (var i = 0; i < recruiterCount; i++) team.push({ id:'r'+i, name:'Recruiter '+i, dept:'d_sc_hr', roleId:'r_sc_req', hrs:40, vacant:false });
          for (var j = 0; j < liveOpenReqs; j++) team.push({ id:'vac'+j, name:'Vacant', dept:'OTHER_SC', roleId:'r_sc_other', hrs:40, vacant:true, _hireEligible:true });
          S = { settings:{orgHeadcount:335,hrBenchmarkRatio:60}, managerSignals:{}, deptVolumes:{},
            roles:[{id:'r_sc_req', dept:'d_sc_hr', name:'Recruiter', supportMeta:{driver:'requisition', reqCapacity: reqCapacity}}], team: team };
        }
        var depReq = { id:'d_sc_hr', name:'Self-Check Support Dept (req)', type:'support', benchmark:null };
        buildReq(60, 35, 2);
        assertField('Support-lane thresholds', 'Requisition lane, 86% of capacity (watch band)', computeDeptSignal(depReq), 'watch', true);
        buildReq(68, 35, 2);
        assertField('Support-lane thresholds', 'Requisition lane, 97% of capacity (over 95%)', computeDeptSignal(depReq), 'flagged', true);

        function buildVol(orgHC, hcCeiling, staffCount) {
          var team = [];
          for (var i = 0; i < staffCount; i++) team.push({ id:'p'+i, name:'Payroll Analyst '+i, dept:'d_sc_hr', roleId:'r_sc_vol', hrs:40, vacant:false });
          S = { settings:{orgHeadcount:orgHC,hrBenchmarkRatio:60}, managerSignals:{}, deptVolumes:{},
            roles:[{id:'r_sc_vol', dept:'d_sc_hr', name:'Payroll Analyst', supportMeta:{driver:'volume', hcCeiling: hcCeiling}}], team: team };
        }
        var depVol = { id:'d_sc_hr', name:'Self-Check Support Dept (vol)', type:'support', benchmark:null };
        buildVol(335, 200, 2);
        assertField('Support-lane thresholds', 'Volume/ceiling lane, 84% of ceiling (watch band)', computeDeptSignal(depVol), 'watch', true);
        buildVol(335, 300, 1);
        assertField('Support-lane thresholds', 'Volume/ceiling lane, 112% of ceiling (over 95%)', computeDeptSignal(depVol), 'flagged', true);

        // Unstaffed lane — demand exists but zero people fill it, while the dept has staff elsewhere
        // (so it clears the earlier, separate not-configured gate, which only checks dept-wide zero).
        S = { settings:{orgHeadcount:335,hrBenchmarkRatio:60}, managerSignals:{}, deptVolumes:{},
          roles:[
            { id:'r_sc_hc2', dept:'d_sc_hr', name:'HR Business Partner', supportMeta:{driver:'headcount'} },
            { id:'r_sc_req2', dept:'d_sc_hr', name:'Recruiter', supportMeta:{driver:'requisition', reqCapacity:35} },
          ],
          team:[{ id:'rec1', name:'Recruiter 1', dept:'d_sc_hr', roleId:'r_sc_req2', hrs:40, vacant:false }],
        };
        var sigUnstaffed = computeDeptSignal(depHC);
        assertField('Support-lane thresholds', 'Headcount lane unstaffed (zero people, role exists)', sigUnstaffed, 'flagged', true);
        assertField('Support-lane thresholds', 'Headcount lane unstaffed (zero people, role exists)', sigUnstaffed, 'anyUnstaffed', true);
      })();

      // ── 5. Manager escalations ──────────────────────────────────────────────
      (function() {
        var depJ = { id:'d_sc_j', name:'Self-Check Judgment Dept', type:'judgment' };
        function buildJ(msig) {
          S = { settings:{orgHeadcount:335,hrBenchmarkRatio:60}, managerSignals:{ d_sc_j: msig }, deptVolumes:{},
            roles:[{id:'r_sc_j', dept:'d_sc_j', name:'Analyst'}],
            team:[{id:'e1', name:'Analyst 1', dept:'d_sc_j', roleId:'r_sc_j', hrs:40, vacant:false}] };
        }
        buildJ({ completeness:'on-track', quality:'meets', mastery:'B' });
        assertField('Manager escalations', 'All good reads', computeDeptSignal(depJ), 'flagged', false);

        buildJ({ completeness:'behind', quality:'meets', mastery:'B' });
        var oneBad = computeDeptSignal(depJ);
        assertField('Manager escalations', 'One bad read alone (behind)', oneBad, 'flagged', false);
        assertField('Manager escalations', 'One bad read alone (behind)', oneBad, 'watch', true);

        buildJ({ completeness:'behind', quality:'below', mastery:'B' });
        assertField('Manager escalations', 'Two bad reads together (behind + below)', computeDeptSignal(depJ), 'flagged', true);

        buildJ({ completeness:'behind', quality:'meets', mastery:'D' });
        assertField('Manager escalations', 'One bad read + weak mastery (behind + D)', computeDeptSignal(depJ), 'flagged', true);

        buildJ({ completeness:'on-track', quality:'meets', mastery:'F' });
        var weakAlone = computeDeptSignal(depJ);
        assertField('Manager escalations', 'Weak mastery alone, no bad read', weakAlone, 'flagged', false);
        assertField('Manager escalations', 'Weak mastery alone, no bad read', weakAlone, 'watch', true);
      })();

      // ── 6. Vacancy eligibility ───────────────────────────────────────────────
      (function() {
        var hireCases = [
          [{status:'active', funding:'operating'}, true],
          [{status:'active', funding:'capital'}, true],
          [{status:'active', funding:'frozen'}, false],
          [{status:'active', funding:'unknown'}, false],
          [{status:'frozen', funding:'operating'}, false],
          [{status:'pending-approval', funding:'operating'}, false],
          [{status:'unknown', funding:'operating'}, false],
        ];
        hireCases.forEach(function(c) {
          var actual = isHireEligible(c[0]);
          record('Vacancy eligibility', 'isHireEligible(' + JSON.stringify(c[0]) + ')', actual === c[1], 'expected ' + c[1] + ', got ' + actual);
        });
        var recruitCases = [
          [{vacant:true}, true],
          [{vacant:true, _hireEligible:true}, true],
          [{vacant:true, _hireEligible:false}, false],
          [{vacant:false}, false],
          [{vacant:false, _hireEligible:true}, false],
        ];
        recruitCases.forEach(function(c) {
          var actual = isVacancyRecruitable(c[0]);
          record('Vacancy eligibility', 'isVacancyRecruitable(' + JSON.stringify(c[0]) + ')', actual === c[1], 'expected ' + c[1] + ', got ' + actual);
        });
      })();

      // ── 7. Business Case verdicts ───────────────────────────────────────────
      (function() {
        var fmt$sc = function(n) { return '$' + Math.round(n).toLocaleString(); };
        function v(sig, opts) { return computeBizCaseVerdict(sig, Object.assign({ fmt$: fmt$sc }, opts)).verdict; }
        record('Business Case verdicts', 'Data Invalid gates the verdict',
          v({ dataInvalid:true, invalidReasons:['x'] }, { hasEligiblePosition:false, financiallyFavorable:false, hireCostFor12Wks:0, ctrCost:0 }) === 'DATA INVALID — CANNOT ASSESS');
        record('Business Case verdicts', 'Not Configured gates the verdict',
          v({ notConfigured:true }, { hasEligiblePosition:false, financiallyFavorable:false, hireCostFor12Wks:0, ctrCost:0 }) === 'DATA INCOMPLETE — CANNOT ASSESS');
        record('Business Case verdicts', 'Flagged + eligible + favorable + hire cheaper → Hire Recommended',
          v({ kind:'volume', flagged:true, watch:false }, { hasEligiblePosition:true, financiallyFavorable:true, hireCostFor12Wks:10000, ctrCost:20000 }) === 'REVIEW: HIRE RECOMMENDED');
        record('Business Case verdicts', 'Flagged + eligible + contractor cheaper → Contractor First',
          v({ kind:'volume', flagged:true, watch:false }, { hasEligiblePosition:true, financiallyFavorable:true, hireCostFor12Wks:20000, ctrCost:10000 }) === 'CONTRACTOR FIRST');
        record('Business Case verdicts', 'Flagged, no eligible position → Position Request Recommended',
          v({ kind:'volume', flagged:true, watch:false }, { hasEligiblePosition:false, financiallyFavorable:true, hireCostFor12Wks:10000, ctrCost:20000 }) === 'POSITION REQUEST RECOMMENDED');
        record('Business Case verdicts', 'Watch only, eligible position exists → Monitor',
          v({ kind:'support', flagged:false, watch:true }, { hasEligiblePosition:true, financiallyFavorable:false, hireCostFor12Wks:0, ctrCost:0 }) === 'MONITOR');
        record('Business Case verdicts', 'Coverage flagged → Operational Intervention Required',
          v({ kind:'coverage', flagged:true, watch:false }, { hasEligiblePosition:false, financiallyFavorable:false, hireCostFor12Wks:0, ctrCost:0 }) === 'OPERATIONAL INTERVENTION REQUIRED');
        record('Business Case verdicts', 'Manager-escalated judgment dept → Further Diagnosis Required',
          v({ kind:'judgment', flagged:false, watch:true, managerEscalated:true }, { hasEligiblePosition:false, financiallyFavorable:false, hireCostFor12Wks:0, ctrCost:0 }) === 'FURTHER DIAGNOSIS REQUIRED');
        record('Business Case verdicts', 'Healthy, nothing flagged → Monitor',
          v({ kind:'volume', flagged:false, watch:false }, { hasEligiblePosition:false, financiallyFavorable:false, hireCostFor12Wks:0, ctrCost:0 }) === 'MONITOR');
      })();

      // ── 8. Data Invalid behavior ─────────────────────────────────────────────
      (function() {
        var dep = { id:'d_sc_di', name:'Self-Check Data-Invalid Dept', type:'support', benchmark:null };
        function buildBase() {
          return {
            settings: { orgHeadcount: 335, hrBenchmarkRatio: 60 }, managerSignals: {}, deptVolumes: {},
            roles: [
              { id:'r_sc_di_hc', dept:'d_sc_di', name:'HRBP', supportMeta:{driver:'headcount'} },
              { id:'r_sc_di_req', dept:'d_sc_di', name:'Recruiter', supportMeta:{driver:'requisition', reqCapacity:35} },
              { id:'r_sc_di_vol', dept:'d_sc_di', name:'Payroll Analyst', supportMeta:{driver:'volume', hcCeiling:300} },
            ],
            team: [
              { id:'e_sc1', dept:'d_sc_di', roleId:'r_sc_di_hc', hrs:40, vacant:false, name:'Person 1' },
              { id:'e_sc2', dept:'d_sc_di', roleId:'r_sc_di_req', hrs:40, vacant:false, name:'Person 2' },
              { id:'e_sc3', dept:'d_sc_di', roleId:'r_sc_di_vol', hrs:40, vacant:false, name:'Person 3' },
            ],
          };
        }
        S = buildBase();
        assertField('Data Invalid behavior', 'Valid support inputs pass through', computeDeptSignal(dep), 'dataInvalid', undefined);

        [
          ['orgHeadcount = 0',        function(s){ s.settings.orgHeadcount = 0; }],
          ['orgHeadcount = -5',       function(s){ s.settings.orgHeadcount = -5; }],
          ['orgHeadcount = NaN',      function(s){ s.settings.orgHeadcount = NaN; }],
          ['orgHeadcount = "335"',    function(s){ s.settings.orgHeadcount = '335'; }],
          ['requisition capacity = 0',   function(s){ s.roles[1].supportMeta.reqCapacity = 0; }],
          ['requisition capacity = NaN', function(s){ s.roles[1].supportMeta.reqCapacity = NaN; }],
          ['payroll ceiling = 0',        function(s){ s.roles[2].supportMeta.hcCeiling = 0; }],
          ['payroll ceiling = -1',       function(s){ s.roles[2].supportMeta.hcCeiling = -1; }],
          ['staffing benchmark = NaN',   function(s){ s.settings.hrBenchmarkRatio = NaN; }],
          ['employee hrs = NaN',         function(s){ s.team[0].hrs = NaN; }],
        ].forEach(function(c) {
          S = buildBase();
          c[1](S);
          var sig = computeDeptSignal(dep);
          record('Data Invalid behavior', c[0] + ' → gates to Data Invalid', sig.dataInvalid === true, 'got dataInvalid=' + sig.dataInvalid + (sig.invalidReasons ? ', reasons: ' + sig.invalidReasons.join('; ') : ''));
        });
      })();

      // ── 9. Time-period volume basis ─────────────────────────────────────────
      (function() {
        function buildTP(depOverrides) {
          S = {
            settings: { orgHeadcount: 335, hrBenchmarkRatio: 60 }, managerSignals: {}, deptVolumes: {},
            roles: [{ id:'r_sc_tp', dept:'d_sc_tp', name:'Analyst' }],
            team: [
              { id:'e1', dept:'d_sc_tp', roleId:'r_sc_tp', hrs:40, vacant:false },
              { id:'e2', dept:'d_sc_tp', roleId:'r_sc_tp', hrs:40, vacant:false },
            ],
          };
          return Object.assign({ id:'d_sc_tp', name:'Self-Check Time-Period Dept', type:'service', fteTrig:.5 }, depOverrides);
        }

        // Default basis unaffected: no volPeriodBasis set behaves exactly as before (already
        // covered by the Settlement fixture above, in category 1 — no separate assertion needed
        // here beyond confirming the field simply defaults to 'current').
        var depDefault = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:180}] });
        assertField('Time-period volume basis', 'No basis set defaults to "current"', computeDeptSignal(depDefault), 'volPeriodBasis', 'current');

        // rolling3 — correctly dated, current reporting period explicit (not the computer's clock)
        var depRoll = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:400}], volPeriodBasis:'rolling3', volCurrentPeriod:'2026-06',
          volPeriodHistory:[{date:'2026-04',values:[200]},{date:'2026-05',values:[300]}] });
        var sigRoll = computeDeptSignal(depRoll);
        assertField('Time-period volume basis', 'Rolling 3-month average, sufficient consecutive history', sigRoll, 'impliedFTE', 3);
        assertField('Time-period volume basis', 'Rolling 3-month average, sufficient consecutive history', sigRoll, 'flagged', true);

        // Same data, entered in reverse array order — must produce an IDENTICAL result, proving
        // the lookup is by actual calendar date and not by array/insertion position.
        var depRollOOO = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:400}], volPeriodBasis:'rolling3', volCurrentPeriod:'2026-06',
          volPeriodHistory:[{date:'2026-05',values:[300]},{date:'2026-04',values:[200]}] });
        assertField('Time-period volume basis', 'Rolling 3-month average, same periods entered out of array order', computeDeptSignal(depRollOOO), 'impliedFTE', 3);

        var depRollShort = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:400}], volPeriodBasis:'rolling3', volCurrentPeriod:'2026-06',
          volPeriodHistory:[{date:'2026-05',values:[200]}] });
        assertField('Time-period volume basis', 'Rolling 3-month average, only 1 of 2 required prior periods', computeDeptSignal(depRollShort), 'dataInvalid', true);

        // The exact bug independently found: two "prior" rows that are undated/years apart still
        // getting silently averaged in as if they were the two immediately preceding months.
        var depRollGap = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:400}], volPeriodBasis:'rolling3', volCurrentPeriod:'2026-06',
          volPeriodHistory:[{date:'2023-01',values:[999]},{date:'2026-05',values:[300]}] }); // missing 2026-04; 2023-01 is not a substitute
        assertField('Time-period volume basis', 'Rolling 3-month average, one recorded period is years away from the required month', computeDeptSignal(depRollGap), 'dataInvalid', true);

        var depRollDupe = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:400}], volPeriodBasis:'rolling3', volCurrentPeriod:'2026-06',
          volPeriodHistory:[{date:'2026-05',values:[300]},{date:'2026-05',values:[999]}] });
        assertField('Time-period volume basis', 'Rolling 3-month average, duplicate dated periods', computeDeptSignal(depRollDupe), 'dataInvalid', true);

        var depRollNoCurrentPeriod = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:400}], volPeriodBasis:'rolling3',
          volPeriodHistory:[{date:'2026-04',values:[200]},{date:'2026-05',values:[300]}] }); // volCurrentPeriod not set
        assertField('Time-period volume basis', 'Rolling 3-month average, no current reporting period set (must not fall back to today\'s date)', computeDeptSignal(depRollNoCurrentPeriod), 'dataInvalid', true);

        var depRollSelfDated = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:400}], volPeriodBasis:'rolling3', volCurrentPeriod:'2026-06',
          volPeriodHistory:[{date:'2026-04',values:[200]},{date:'2026-06',values:[999]}] }); // a history row duplicates the current period itself
        assertField('Time-period volume basis', 'Rolling 3-month average, a history row is dated to the current reporting period itself', computeDeptSignal(depRollSelfDated), 'dataInvalid', true);

        // seasonalPeak — "same period last year," anchored to the department's explicit current
        // reporting period, never to the computer's real-world clock.
        var depPeak = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:180}], volPeriodBasis:'seasonalPeak', volCurrentPeriod:'2026-06',
          volPeriodHistory:[{date:'2025-03',values:[150]},{date:'2025-06',values:[500]},{date:'2025-09',values:[200]}] });
        var sigPeak = computeDeptSignal(depPeak);
        assertField('Time-period volume basis', 'Seasonal peak matches June 2025 exactly (not the highest value on record, not current)', sigPeak, 'impliedFTE', 5);
        var depPeakNoMatch = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:180}], volPeriodBasis:'seasonalPeak', volCurrentPeriod:'2026-06',
          volPeriodHistory:[{date:'2025-03',values:[150]},{date:'2025-09',values:[200]}] }); // no June 2025 entry
        assertField('Time-period volume basis', 'Seasonal peak with no period recorded for the matching month last year', computeDeptSignal(depPeakNoMatch), 'dataInvalid', true);
        var depPeakNoHist = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:180}], volPeriodBasis:'seasonalPeak', volCurrentPeriod:'2026-06', volPeriodHistory:[] });
        assertField('Time-period volume basis', 'Seasonal peak with no recorded history at all', computeDeptSignal(depPeakNoHist), 'dataInvalid', true);
        var depPeakNoCurrentPeriod = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:180}], volPeriodBasis:'seasonalPeak',
          volPeriodHistory:[{date:'2025-06',values:[500]}] }); // volCurrentPeriod not set — must not fall back to the real clock
        assertField('Time-period volume basis', 'Seasonal peak with no current reporting period set', computeDeptSignal(depPeakNoCurrentPeriod), 'dataInvalid', true);

        // forecastSustained
        var depFcst = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:180}], volPeriodBasis:'forecastSustained', volForecastValues:[350] });
        assertField('Time-period volume basis', 'Forecasted sustained increase, valid forecast', computeDeptSignal(depFcst), 'impliedFTE', 4);
        var depFcstMissing = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:180}], volPeriodBasis:'forecastSustained', volForecastValues:[] });
        assertField('Time-period volume basis', 'Forecasted sustained increase, no forecast entered', computeDeptSignal(depFcstMissing), 'dataInvalid', true);

        // consecutiveN — the case this whole feature exists for: a single busy period must not
        // alone produce Flagged.
        var depConsecShort = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:260}], volPeriodBasis:'consecutiveN', volConsecutiveN:3, volCurrentPeriod:'2026-06',
          volPeriodHistory:[{date:'2026-05',values:[250]}] });
        var sigConsecShort = computeDeptSignal(depConsecShort);
        assertField('Time-period volume basis', 'Consecutive-periods basis, only 2 of 3 confirmed → Watch not Flagged', sigConsecShort, 'flagged', false);
        assertField('Time-period volume basis', 'Consecutive-periods basis, only 2 of 3 confirmed → Watch not Flagged', sigConsecShort, 'watch', true);
        assertField('Time-period volume basis', 'Consecutive-periods basis, only 2 of 3 confirmed → Watch not Flagged', sigConsecShort, 'consecutiveStreak', 2);

        var depConsecFull = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:260}], volPeriodBasis:'consecutiveN', volConsecutiveN:3, volCurrentPeriod:'2026-06',
          volPeriodHistory:[{date:'2026-04',values:[250]},{date:'2026-05',values:[250]}] });
        var sigConsecFull = computeDeptSignal(depConsecFull);
        assertField('Time-period volume basis', 'Consecutive-periods basis, all 3 confirmed → Flagged', sigConsecFull, 'flagged', true);
        assertField('Time-period volume basis', 'Consecutive-periods basis, all 3 confirmed → Flagged', sigConsecFull, 'consecutiveStreak', 3);

        // Same 3-consecutive-month data, entered out of array order — must give an identical result.
        var depConsecOOO = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:260}], volPeriodBasis:'consecutiveN', volConsecutiveN:3, volCurrentPeriod:'2026-06',
          volPeriodHistory:[{date:'2026-05',values:[250]},{date:'2026-04',values:[250]}] });
        assertField('Time-period volume basis', 'Consecutive-periods basis, same 3 months entered out of array order', computeDeptSignal(depConsecOOO), 'consecutiveStreak', 3);

        // The exact bug independently found: a genuine gap in the month sequence (April missing,
        // March present further back) must still break the streak at the gap — March existing
        // elsewhere in the array must NOT be treated as if it were adjacent to May.
        var depConsecGap = buildTP({ volDrivers:[{name:'Widgets',perFTE:100,current:260}], volPeriodBasis:'consecutiveN', volConsecutiveN:3, volCurrentPeriod:'2026-06',
          volPeriodHistory:[{date:'2026-03',values:[250]},{date:'2026-05',values:[250]}] }); // April missing
        var sigConsecGap = computeDeptSignal(depConsecGap);
        assertField('Time-period volume basis', 'Consecutive-periods basis, nonconsecutive dates (March+May, April missing) → streak stops at the gap', sigConsecGap, 'consecutiveStreak', 2);
        assertField('Time-period volume basis', 'Consecutive-periods basis, nonconsecutive dates (March+May, April missing) → streak stops at the gap', sigConsecGap, 'flagged', false);
      })();

      // ── 10. Position register / roster vacancy synchronization ─────────────
      // Regression coverage for a real defect: editing a position's status or approved-seat count
      // directly in Configure → Built Positions must be reflected immediately in every roster-
      // vacancy-based count (Open Reqs, Executive Summary Open Positions, support requisitions),
      // not just in the Built Positions page's own getPlanAllocations() view. Before the fix,
      // these two views could silently diverge because roster vacancy records cached their
      // hire-eligibility at import time and were never refreshed by a later manual edit.
      (function() {
        S = {
          settings: { orgHeadcount: 335, hrBenchmarkRatio: 60 }, managerSignals: {}, deptVolumes: {},
          roles: [{ id: 'r_sc_vac', dept: 'd_sc_vac', name: 'Analyst' }],
          depts: [{ id: 'd_sc_vac', name: 'Self-Check Vacancy Sync Dept', type: 'service', fteTrig: .5 }],
        };
        S.team = [
          { id: 'e_sc_vac1', name: 'Vacant', dept: 'd_sc_vac', roleId: 'r_sc_vac', hrs: 40, vacant: true,
            _source: 'built', _builtPosId: 'PCN-SC-1', _builtStatus: 'active', _builtFunding: 'operating', _hireEligible: true },
        ];
        S.headcountPlan = [
          { id: 'hp_sc_vac1', roleId: 'r_sc_vac', dept: 'd_sc_vac', count: 1, builtPosId: 'PCN-SC-1', status: 'active', funding: 'operating', annualBudget: 80000 },
        ];

        var beforeCount = S.team.filter(isVacancyRecruitable).length;
        record('Position/vacancy sync', 'Baseline: one hire-eligible vacancy matches Open Reqs and Built Positions', beforeCount === 1, 'got ' + beforeCount);

        // Edit the position's status directly, exactly as the Configure UI does — no separate
        // propagation call exists or should be needed; isVacancyRecruitable must resolve live.
        S.headcountPlan[0].status = 'pending-approval';
        var afterStatusEdit = S.team.filter(isVacancyRecruitable).length;
        record('Position/vacancy sync', 'Editing position status to Pending Approval immediately drops Open Reqs to 0', afterStatusEdit === 0, 'got ' + afterStatusEdit);
        var allocAfterStatus = getPlanAllocations()['hp_sc_vac1'];
        record('Position/vacancy sync', 'Built Positions and Open Reqs agree after the status edit (both ineligible)', allocAfterStatus && allocAfterStatus.hireEligible === false, JSON.stringify(allocAfterStatus));

        // Revert status, then increase Approved Seats — must synthesize matching vacancy records.
        S.headcountPlan[0].status = 'active';
        S.headcountPlan[0].count = 3;
        syncVacancyRecordsForRoleDept('r_sc_vac', 'd_sc_vac');
        var afterSeatIncrease = S.team.filter(isVacancyRecruitable).length;
        record('Position/vacancy sync', 'Increasing Approved Seats from 1 to 3 grows Open Reqs to 3', afterSeatIncrease === 3, 'got ' + afterSeatIncrease);

        // Decrease Approved Seats back down — must remove the excess vacancy placeholders. Uses a
        // SEPARATE role/dept pool to check filled-seat safety, so that check doesn't interact with
        // this department's own approved-seat count (a filled person sharing the same role+dept
        // pool would legitimately consume one of the approved seats, which is correct behavior,
        // not a bug — kept isolated here so the two checks don't confound each other).
        S.headcountPlan[0].count = 1;
        syncVacancyRecordsForRoleDept('r_sc_vac', 'd_sc_vac');
        var afterSeatDecrease = S.team.filter(isVacancyRecruitable).length;
        record('Position/vacancy sync', 'Decreasing Approved Seats back to 1 shrinks Open Reqs back to 1', afterSeatDecrease === 1, 'got ' + afterSeatDecrease);

        S.roles.push({ id: 'r_sc_vac_other', dept: 'd_sc_vac', name: 'Other Role' });
        S.headcountPlan.push({ id: 'hp_sc_vac_other', roleId: 'r_sc_vac_other', dept: 'd_sc_vac', count: 1, builtPosId: 'PCN-SC-OTHER', status: 'active', funding: 'operating', annualBudget: 80000 });
        S.team.push({ id: 'e_sc_vac_filled', name: 'Real Person', dept: 'd_sc_vac', roleId: 'r_sc_vac_other', hrs: 40, vacant: false, _source: 'built', _builtPosId: 'PCN-SC-OTHER' });
        syncVacancyRecordsForRoleDept('r_sc_vac_other', 'd_sc_vac');
        var filledStillPresent = S.team.some(function(e) { return e.id === 'e_sc_vac_filled' && !e.vacant; });
        record('Position/vacancy sync', 'A filled seat in an unrelated role is never touched by seat-count synchronization', filledStillPresent === true, 'filled record present: ' + filledStillPresent);
      })();

      // ── 11. Position register / roster sync — two rows sharing one role+dept ───
      // Regression coverage for the specific failure the previous version of this fix still had:
      // pooling by role+dept alone breaks the moment two position rows share that pool with
      // different eligibility — an Active row and a separate Pending Approval row for the same
      // role is a normal, real Built pattern (the live model's own Settlement department has
      // exactly this shape), not a contrived edge case. This replica captures that same shape —
      // two rows, one pre-existing unlinked vacancy, some real filled headcount — without
      // depending on the live seed data itself, so it can't silently stop testing this if the
      // seed data ever changes.
      (function() {
        S = {
          settings: { orgHeadcount: 335, hrBenchmarkRatio: 60 }, managerSignals: {}, deptVolumes: {},
          roles: [{ id: 'r_dual', dept: 'd_dual', name: 'Analyst' }],
          depts: [{ id: 'd_dual', name: 'Dual-Row Self-Check Dept', type: 'service', fteTrig: .5 }],
        };
        S.team = [
          { id: 'e_dual_f1', name: 'Filled One', dept: 'd_dual', roleId: 'r_dual', hrs: 40, vacant: false },
          { id: 'e_dual_f2', name: 'Filled Two', dept: 'd_dual', roleId: 'r_dual', hrs: 40, vacant: false },
          { id: 'e_dual_f3', name: 'Filled Three', dept: 'd_dual', roleId: 'r_dual', hrs: 40, vacant: false },
          // One pre-existing vacancy with NO link at all — exactly like real preloaded/seed data.
          { id: 'e_dual_vac', name: 'Vacant', dept: 'd_dual', roleId: 'r_dual', hrs: 40, vacant: true },
        ];
        S.headcountPlan = [
          { id: 'hp_dual_active', roleId: 'r_dual', dept: 'd_dual', count: 4, status: 'active', funding: 'operating', annualBudget: 85000 },
          { id: 'hp_dual_pending', roleId: 'r_dual', dept: 'd_dual', count: 1, status: 'pending-approval', funding: 'operating', annualBudget: 85000 },
        ];

        function openReqsHere() { return S.team.filter(function(e) { return e.roleId === 'r_dual' && e.dept === 'd_dual'; }).filter(isVacancyRecruitable).length; }
        function hireEligVacHere() {
          var alloc = getPlanAllocations();
          return (alloc['hp_dual_active'].hireEligible ? alloc['hp_dual_active'].vacant : 0) + (alloc['hp_dual_pending'].hireEligible ? alloc['hp_dual_pending'].vacant : 0);
        }

        record('Position/vacancy sync', 'Two-row baseline: Open Reqs and Hire-Eligible Vacancies agree (both 1)', openReqsHere() === 1 && hireEligVacHere() === 1, 'openReqs=' + openReqsHere() + ' hireElig=' + hireEligVacHere());

        // Change the ACTIVE row to Pending Approval — status only, no count change.
        S.headcountPlan[0].status = 'pending-approval';
        syncVacancyRecordsForRoleDept('r_dual', 'd_dual');
        record('Position/vacancy sync', 'Active row -> Pending Approval: both Open Reqs and Hire-Eligible Vacancies drop to 0', openReqsHere() === 0 && hireEligVacHere() === 0, 'openReqs=' + openReqsHere() + ' hireElig=' + hireEligVacHere());

        // Revert, then increase the active row's seats — must NOT create a phantom seat on the
        // still-pending row, and must NOT reuse a vacancy that belongs to the other row.
        S.headcountPlan[0].status = 'active';
        S.headcountPlan[0].count = 5;
        syncVacancyRecordsForRoleDept('r_dual', 'd_dual');
        record('Position/vacancy sync', 'Revert + increase Active row seats: both become 2', openReqsHere() === 2 && hireEligVacHere() === 2, 'openReqs=' + openReqsHere() + ' hireElig=' + hireEligVacHere());

        // Increase ONLY the Pending Approval row — total vacant seats rise, recruitable Open Reqs
        // must NOT, since the new seat belongs to the still-ineligible row.
        S.headcountPlan[1].count = 2;
        syncVacancyRecordsForRoleDept('r_dual', 'd_dual');
        var totalVacantNow = S.team.filter(function(e) { return e.roleId === 'r_dual' && e.dept === 'd_dual' && e.vacant && !e._builtStale; }).length;
        record('Position/vacancy sync', 'Increasing only the Pending row grows total vacant seats to 4 but Open Reqs stays at 2', totalVacantNow === 4 && openReqsHere() === 2, 'totalVacant=' + totalVacantNow + ' openReqs=' + openReqsHere());

        // Reduce both rows back down — confirm the correct linked vacancies are removed, not
        // whichever record happens to appear first.
        S.headcountPlan[0].count = 4;
        S.headcountPlan[1].count = 1;
        syncVacancyRecordsForRoleDept('r_dual', 'd_dual');
        var remaining = S.team.filter(function(e) { return e.roleId === 'r_dual' && e.dept === 'd_dual' && e.vacant && !e._builtStale; });
        var linkedActive = remaining.filter(function(e) { return e._planId === 'hp_dual_active'; }).length;
        var linkedPending = remaining.filter(function(e) { return e._planId === 'hp_dual_pending'; }).length;
        record('Position/vacancy sync', 'Reducing both rows back down leaves exactly one vacancy correctly linked to each row', linkedActive === 1 && linkedPending === 1, 'linkedActive=' + linkedActive + ' linkedPending=' + linkedPending);
        record('Position/vacancy sync', 'Open Reqs correctly back to 1 (only the Active row\'s seat)', openReqsHere() === 1, 'got ' + openReqsHere());
      })();

      // ── 12. Synchronization idempotency ─────────────────────────────────────
      // Regression coverage for a real defect found in review: a separate manual "Add Vacancy"
      // action used to be able to create a genuine duplicate — a second vacant record for a seat
      // that synchronization had already accounted for, which desynced Position Register counts
      // from Open Reqs immediately, not just after a later edit. That action has been removed
      // entirely (Configure -> Built Positions is now the only place a vacancy is created), but
      // the underlying property this protects — running sync twice never creates a duplicate or
      // changes anything on the second run — is worth testing directly and permanently, since
      // syncAllVacancyRecords now runs on every page load and every config load.
      (function() {
        S = {
          settings: { orgHeadcount: 335, hrBenchmarkRatio: 60 }, managerSignals: {}, deptVolumes: {},
          roles: [{ id: 'r_idem', dept: 'd_idem', name: 'Analyst' }],
          depts: [{ id: 'd_idem', name: 'Idempotency Self-Check Dept', type: 'service', fteTrig: .5 }],
        };
        S.team = [
          { id: 'e_idem_f1', name: 'Filled One', dept: 'd_idem', roleId: 'r_idem', hrs: 40, vacant: false },
          // One pre-existing UNLINKED vacancy — same shape as a real preloaded/legacy record.
          { id: 'e_idem_vac', name: 'Vacant', dept: 'd_idem', roleId: 'r_idem', hrs: 40, vacant: true },
        ];
        S.headcountPlan = [
          { id: 'hp_idem_active', roleId: 'r_idem', dept: 'd_idem', count: 3, status: 'active', funding: 'operating', annualBudget: 85000 },
          { id: 'hp_idem_pending', roleId: 'r_idem', dept: 'd_idem', count: 1, status: 'pending-approval', funding: 'operating', annualBudget: 85000 },
        ];

        function teamShape() {
          var scoped = S.team.filter(function(e) { return e.roleId === 'r_idem' && e.dept === 'd_idem'; });
          return { count: scoped.length, ids: scoped.map(function(e) { return e.id; }).sort().join(','), openReqs: scoped.filter(isVacancyRecruitable).length };
        }

        syncVacancyRecordsForRoleDept('r_idem', 'd_idem');
        var afterFirst = teamShape();
        record('Position/vacancy sync', 'Idempotency: first sync on fresh unlinked data links/creates as expected (1 filled + 3 vacant across both rows = 4 total)', afterFirst.count === 4, 'got ' + afterFirst.count);

        syncVacancyRecordsForRoleDept('r_idem', 'd_idem');
        var afterSecond = teamShape();
        record('Position/vacancy sync', 'Idempotency: running sync again with no position changes creates NO duplicate (same count)', afterSecond.count === afterFirst.count, 'first=' + afterFirst.count + ' second=' + afterSecond.count);
        record('Position/vacancy sync', 'Idempotency: the exact same records persist (same ids, not new ones)', afterSecond.ids === afterFirst.ids, 'first=' + afterFirst.ids + ' second=' + afterSecond.ids);
        record('Position/vacancy sync', 'Idempotency: Open Reqs unchanged by the redundant second run', afterSecond.openReqs === afterFirst.openReqs, 'first=' + afterFirst.openReqs + ' second=' + afterSecond.openReqs);

        // Also confirm syncAllVacancyRecords (the page-load entry point) is itself idempotent when
        // called repeatedly, and correctly links the pre-existing unlinked seed vacancy without
        // needing any position edit first — this is what fixes "sample vacancy not yet linked on
        // initial load."
        var linkedAfterAll = S.team.find(function(e) { return e.id === 'e_idem_vac'; })._planId;
        record('Position/vacancy sync', 'Reconciliation links the pre-existing sample vacancy without requiring any edit first', !!linkedAfterAll, 'linked to: ' + linkedAfterAll);
        syncAllVacancyRecords();
        var afterAllTwice = teamShape();
        record('Position/vacancy sync', 'syncAllVacancyRecords is also idempotent end-to-end', afterAllTwice.count === afterFirst.count && afterAllTwice.ids === afterFirst.ids, 'got ' + afterAllTwice.count);
      })();

      // ── 13. Active Projects — live-signal integration ───────────────────────
      // Regression coverage for wiring Active Projects (Team & Workload → Active Projects) into implied FTE
      // for volume-threshold departments, added after the driver MAX/SUM combine, rounded once
      // independently — plus confirming it stays purely informational (never touches status) for
      // department types with no honest way to fold temporary project hours into their formula.
      (function() {
        S = {
          settings: { orgHeadcount: 335, hrBenchmarkRatio: 60 }, managerSignals: {}, deptVolumes: {},
          roles: [{ id: 'r_proj', dept: 'd_proj', name: 'Analyst' }],
          depts: [{ id: 'd_proj', name: 'Active Projects Self-Check Dept', type: 'service', fteTrig: .5,
            volDrivers: [{ name: 'Widgets', perFTE: 100, current: 500 }] }], // implies ceil(500/100)=5 FTE on its own
          acts: [{ name: 'Core', billable: true }],
        };
        S.team = [
          { id: 'e_proj_1', name: 'Staff One', dept: 'd_proj', roleId: 'r_proj', hrs: 40, vacant: false },
          { id: 'e_proj_2', name: 'Staff Two', dept: 'd_proj', roleId: 'r_proj', hrs: 40, vacant: false },
          { id: 'e_proj_3', name: 'Staff Three', dept: 'd_proj', roleId: 'r_proj', hrs: 40, vacant: false },
          { id: 'e_proj_4', name: 'Staff Four', dept: 'd_proj', roleId: 'r_proj', hrs: 40, vacant: false },
          { id: 'e_proj_5', name: 'Staff Five', dept: 'd_proj', roleId: 'r_proj', hrs: 40, vacant: false },
        ]; // 5 productive FTE — matches the 5 FTE implied by volume exactly, so baseline is Healthy
        S.impls = [];
        var d_proj = S.depts[0];

        var sigNoProject = computeDeptSignal(d_proj);
        record('Active Projects', 'No active projects — baseline implied FTE from volume only (5)', sigNoProject.impliedFTE === 5 && sigNoProject.impliedFTE_fromProjects === 0, 'got impliedFTE=' + sigNoProject.impliedFTE);
        record('Active Projects', 'No active projects — department reads Healthy (gap 0)', !sigNoProject.flagged && !sigNoProject.watch, 'flagged=' + sigNoProject.flagged + ' watch=' + sigNoProject.watch);

        S.impls.push({ id: 'sc_proj_1', name: 'Self-Check Project', dept: 'd_proj', actId: 'a0', hrs: 20, wksLeft: 5 });
        var sigWithProject = computeDeptSignal(d_proj);
        record('Active Projects', 'One 20hr/wk active project adds a fractional 0.5 FTE, NOT a ceiled whole FTE', sigWithProject.impliedFTE_fromProjects === 0.5, 'got ' + sigWithProject.impliedFTE_fromProjects);
        record('Active Projects', 'Total implied FTE becomes 5.5 (5 from volume + 0.5 from project)', sigWithProject.impliedFTE === 5.5, 'got ' + sigWithProject.impliedFTE);
        record('Active Projects', 'A previously-Healthy department can still correctly Flag once the fractional gap crosses its own threshold', sigWithProject.flagged === true, 'flagged=' + sigWithProject.flagged);

        S.impls[0].wksLeft = 0;
        var sigExpiredProject = computeDeptSignal(d_proj);
        record('Active Projects', 'A project with wksLeft = 0 contributes nothing — back to baseline', sigExpiredProject.impliedFTE === 5 && !sigExpiredProject.flagged, 'got impliedFTE=' + sigExpiredProject.impliedFTE);

        // A reviewer caught this exact case: the earlier version ceiled the project component to
        // a whole FTE, so a trivial 1-hour/week project added a full phantom FTE — identical to a
        // 40-hour/week project. Permanent regression coverage for the fix: a tiny project must
        // contribute a correspondingly tiny fractional amount, nothing close to a whole FTE, and
        // must NOT be able to flip a comfortably-Healthy department on its own.
        S.impls = [{ id: 'sc_proj_tiny', name: 'Trivial Project', dept: 'd_proj', actId: 'a0', hrs: 1, wksLeft: 2 }];
        var sigTinyProject = computeDeptSignal(d_proj);
        record('Active Projects', 'A 1-hour/week project contributes ~0.03 FTE, nowhere near a full FTE', sigTinyProject.impliedFTE_fromProjects < 0.1, 'got ' + sigTinyProject.impliedFTE_fromProjects);
        record('Active Projects', 'That tiny addition does not incorrectly escalate to Flagged (a sliver of Watch is correct and honest, not a bug)', !sigTinyProject.flagged, 'flagged=' + sigTinyProject.flagged + ' watch=' + sigTinyProject.watch);

        // Multiple small projects must still sum BEFORE rounding, not round-then-sum.
        S.impls = [
          { id: 'sc_proj_a', name: 'A', dept: 'd_proj', actId: 'a0', hrs: 15, wksLeft: 3 },
          { id: 'sc_proj_b', name: 'B', dept: 'd_proj', actId: 'a0', hrs: 15, wksLeft: 3 },
        ];
        var sigTwoSmall = computeDeptSignal(d_proj);
        record('Active Projects', 'Two 15hr projects sum to 30hrs first (0.75 FTE), not rounded per-project then summed', sigTwoSmall.impliedFTE_fromProjects === 0.75, 'got ' + sigTwoSmall.impliedFTE_fromProjects);

        // A department type with no honest formula mapping (judgment) must never have its status
        // affected by an active project, even though the project itself should remain visible.
        S.depts.push({ id: 'd_proj_judgment', name: 'Judgment Self-Check Dept', type: 'judgment' });
        S.impls.push({ id: 'sc_proj_judg', name: 'Judgment dept project', dept: 'd_proj_judgment', actId: 'a0', hrs: 40, wksLeft: 4 });
        var sigJudgment = computeDeptSignal(S.depts[1]);
        record('Active Projects', 'A judgment department\'s status is never affected by an active project (no formula exists to feed)', sigJudgment.kind === 'judgment' && !sigJudgment.flagged && !sigJudgment.watch, 'kind=' + sigJudgment.kind);
        var judgmentNote = activeProjectsNote('d_proj_judgment');
        record('Active Projects', 'But the project remains visible via activeProjectsNote for that department', judgmentNote.indexOf('1 active project') !== -1, 'note empty: ' + !judgmentNote);
      })();

      // ── 14. Anticipated Demand (Pipeline) — forward-look, never blended into today ──────────
      // Regression coverage for the deliberate design choice that Pipeline is probabilistic FUTURE
      // business, not confirmed current work — unlike Active Projects, it must NEVER change
      // impliedFTE/volGap/flagged/watch for TODAY. It only ever produces a separate,
      // clearly-labeled forwardImpliedFTE figure, scoped to the model's own Forward Planning
      // Horizon and probability-weighted the same way the legacy engine already does.
      (function() {
        S = {
          settings: { orgHeadcount: 335, hrBenchmarkRatio: 60, hz: 12 }, managerSignals: {}, deptVolumes: {},
          roles: [{ id: 'r_pipe', dept: 'd_pipe', name: 'Analyst' }],
          depts: [{ id: 'd_pipe', name: 'Pipeline Self-Check Dept', type: 'service', fteTrig: .5,
            volDrivers: [{ name: 'Widgets', perFTE: 100, current: 500 }] }], // implies 5 FTE on its own
          acts: [{ name: 'Core', billable: true }],
        };
        S.team = [
          { id: 'e_pipe_1', name: 'Staff One', dept: 'd_pipe', roleId: 'r_pipe', hrs: 40, vacant: false },
          { id: 'e_pipe_2', name: 'Staff Two', dept: 'd_pipe', roleId: 'r_pipe', hrs: 40, vacant: false },
          { id: 'e_pipe_3', name: 'Staff Three', dept: 'd_pipe', roleId: 'r_pipe', hrs: 40, vacant: false },
          { id: 'e_pipe_4', name: 'Staff Four', dept: 'd_pipe', roleId: 'r_pipe', hrs: 40, vacant: false },
          { id: 'e_pipe_5', name: 'Staff Five', dept: 'd_pipe', roleId: 'r_pipe', hrs: 40, vacant: false },
        ]; // 5 productive FTE = 5 implied FTE — baseline Healthy
        S.pipeline = [];
        S.impls = [];
        var d_pipe = S.depts[0];

        var sigNoPipe = computeDeptSignal(d_pipe);
        record('Anticipated Demand', 'No pipeline items — baseline Healthy, forwardImpliedFTE equals impliedFTE', !sigNoPipe.flagged && !sigNoPipe.watch && sigNoPipe.forwardImpliedFTE === sigNoPipe.impliedFTE, 'flagged=' + sigNoPipe.flagged);

        // A large, high-probability, NEAR-TERM deal — must still not move today's status.
        S.pipeline.push({ id: 'sc_pipe_1', name: 'Self-Check Deal', dept: 'd_pipe', svcHrs: 80, prob: 1.0, closeWk: 3 });
        var sigWithPipe = computeDeptSignal(d_pipe);
        record('Anticipated Demand', 'A large near-certain near-term deal does NOT change today\'s implied FTE (still 5)', sigWithPipe.impliedFTE === 5, 'got ' + sigWithPipe.impliedFTE);
        record('Anticipated Demand', 'Today\'s status is still Healthy despite the pending deal', !sigWithPipe.flagged && !sigWithPipe.watch, 'flagged=' + sigWithPipe.flagged + ' watch=' + sigWithPipe.watch);
        record('Anticipated Demand', 'forwardImpliedFTE correctly reflects it: 80hrs*1.0/40=2 added to 5 = 7', Math.abs(sigWithPipe.forwardImpliedFTE - 7) < 1e-9, 'got ' + sigWithPipe.forwardImpliedFTE);

        // A deal beyond the Forward Planning Horizon contributes nothing at all.
        S.pipeline = [{ id: 'sc_pipe_far', name: 'Distant Deal', dept: 'd_pipe', svcHrs: 80, prob: 1.0, closeWk: 50 }];
        var sigFarPipe = computeDeptSignal(d_pipe);
        record('Anticipated Demand', 'A deal beyond the Forward Planning Horizon (12 wks) contributes nothing', sigFarPipe.pipelineForwardFTE === 0 && sigFarPipe.pipelineItemCount === 0, 'got ' + sigFarPipe.pipelineForwardFTE);

        // Probability weighting applies correctly.
        S.pipeline = [{ id: 'sc_pipe_half', name: 'Coin Flip', dept: 'd_pipe', svcHrs: 40, prob: 0.5, closeWk: 2 }];
        var sigHalf = computeDeptSignal(d_pipe);
        record('Anticipated Demand', 'A 50%-probability 40hr/wk deal weights to 20hrs = 0.5 FTE, not the full 1.0', Math.abs(sigHalf.pipelineForwardFTE - 0.5) < 1e-9, 'got ' + sigHalf.pipelineForwardFTE);

        // A department type with no formula (judgment) must never have status affected, but the
        // deal should remain visible.
        S.depts.push({ id: 'd_pipe_judgment', name: 'Judgment Pipeline Self-Check Dept', type: 'judgment' });
        S.pipeline.push({ id: 'sc_pipe_judg', name: 'Judgment dept deal', dept: 'd_pipe_judgment', svcHrs: 40, prob: 1.0, closeWk: 4 });
        var sigJudgment = computeDeptSignal(S.depts[1]);
        record('Anticipated Demand', 'A judgment department\'s status is never affected by a pipeline deal', sigJudgment.kind === 'judgment' && !sigJudgment.flagged && !sigJudgment.watch, 'kind=' + sigJudgment.kind);
        var judgmentPipeNote = pipelineNote('d_pipe_judgment');
        record('Anticipated Demand', 'But the deal remains visible via pipelineNote for that department', judgmentPipeNote.indexOf('1 anticipated deal') !== -1, 'note empty: ' + !judgmentPipeNote);
      })();

      // ── 15. Scenario Compare — live-signal snapshot capture ─────────────────
      // Regression coverage for _captureLiveMetrics: confirms a captured scenario correctly
      // records each department's real live status (not the legacy calcDept metrics, which were
      // already the only thing this page captured before), and that computeDeptSeverity resolves
      // consistently for the same signal it was already proven correct against elsewhere.
      (function() {
        S = {
          settings: { orgHeadcount: 335, hrBenchmarkRatio: 60 }, managerSignals: {}, deptVolumes: {},
          roles: [{ id: 'r_cmp', dept: 'd_cmp_sc', name: 'Analyst' }],
          depts: [{ id: 'd_cmp_sc', name: 'Compare Self-Check Dept', type: 'service', fteTrig: .5,
            volDrivers: [{ name: 'Widgets', perFTE: 100, current: 500 }] }],
          acts: [{ name: 'Core', billable: true }], impls: [], pipeline: [], clients: [],
        };
        S.team = [
          { id: 'e_cmp_1', name: 'Staff One', dept: 'd_cmp_sc', roleId: 'r_cmp', hrs: 40, vacant: false },
          { id: 'e_cmp_2', name: 'Staff Two', dept: 'd_cmp_sc', roleId: 'r_cmp', hrs: 40, vacant: false },
          { id: 'e_cmp_3', name: 'Staff Three', dept: 'd_cmp_sc', roleId: 'r_cmp', hrs: 40, vacant: false },
          { id: 'e_cmp_4', name: 'Staff Four', dept: 'd_cmp_sc', roleId: 'r_cmp', hrs: 40, vacant: false },
          { id: 'e_cmp_5', name: 'Staff Five', dept: 'd_cmp_sc', roleId: 'r_cmp', hrs: 40, vacant: false },
        ]; // 5 productive FTE = 5 implied — baseline Healthy

        var liveSnap = _captureLiveMetrics();
        record('Scenario Compare', 'Live snapshot captures the correct status for a Healthy department', liveSnap['d_cmp_sc'].statusLabel === 'Healthy', 'got ' + liveSnap['d_cmp_sc'].statusLabel);
        record('Scenario Compare', 'Live snapshot severity score is low for a Healthy department', liveSnap['d_cmp_sc'].severity <= 10, 'got ' + liveSnap['d_cmp_sc'].severity);

        S.depts[0].volDrivers[0].current = 900; // implies 9 FTE vs 5 productive — well past Flagged
        var liveSnap2 = _captureLiveMetrics();
        record('Scenario Compare', 'A real department edit is reflected in a freshly captured live snapshot', liveSnap2['d_cmp_sc'].statusLabel === 'Flagged', 'got ' + liveSnap2['d_cmp_sc'].statusLabel);
        record('Scenario Compare', 'Severity score increased to reflect the worse state', liveSnap2['d_cmp_sc'].severity > liveSnap['d_cmp_sc'].severity, 'before=' + liveSnap['d_cmp_sc'].severity + ' after=' + liveSnap2['d_cmp_sc'].severity);
      })();

      // ── 16. Weekly Impact — hours/week translation, never fabricated ────────
      (function() {
        S = {
          settings: { orgHeadcount: 335, hrBenchmarkRatio: 60 }, managerSignals: {}, deptVolumes: {},
          roles: [{ id: 'r_wi', dept: 'd_wi', name: 'Analyst' }],
          depts: [{ id: 'd_wi', name: 'Weekly Impact Self-Check Dept', type: 'service', fteTrig: .5, approvedForReporting: true,
            volDrivers: [{ name: 'Widgets', perFTE: 100, current: 900 }] }], // implies 9 FTE vs 5 productive = Flagged
          acts: [{ name: 'Core', billable: true }], impls: [], pipeline: [], haircut: { pto:0, hol:0, sick:0, interrupt:0 },
        };
        S.team = [1,2,3,4,5].map(function(i){ return { id: 'e_wi_'+i, name: 'Staff '+i, dept: 'd_wi', roleId: 'r_wi', hrs: 40, vacant: false }; });
        S.statusHistory = {};

        var wi = computeWeeklyImpact();
        record('Weekly Impact', 'Flagged department correctly converts FTE gap to hours/week (4 FTE * 40 = 160)', wi.hrsShort === 160, 'got ' + wi.hrsShort);
        record('Weekly Impact', 'No trend without real history — never fabricated', wi.trend === null, 'trend=' + JSON.stringify(wi.trend));

        var pastDate = new Date(); pastDate.setDate(pastDate.getDate() - 7);
        S.statusHistory['d_wi'] = [{ date: pastDate.toISOString().slice(0,10), kind:'volume', flagged:true, watch:false, dataInvalid:false, notConfigured:false, severity:80, volGap: 6 }];
        var wi2 = computeWeeklyImpact();
        record('Weekly Impact', 'With genuine 7-day-old history, trend populates and correctly reads improving (was 240 hrs/wk, now 160)', wi2.trend !== null && wi2.trend.direction === 'improving', 'got ' + JSON.stringify(wi2.trend));
      })();

      // ── 17. Approval-for-reporting gate — a reviewer's #1 finding ────────────
      // An unapproved department's real Flagged/Watch status must never inflate a number
      // presented as an organizational finding (Weekly Impact, Needs a Decision, Monitoring).
      // It must also never be silently hidden — it should still be visible, just walled off.
      (function() {
        S = {
          settings: { orgHeadcount: 335, hrBenchmarkRatio: 60 }, managerSignals: {}, deptVolumes: {},
          roles: [
            { id: 'r_appr', dept: 'd_approved', name: 'Analyst' },
            { id: 'r_pend', dept: 'd_pending', name: 'Analyst' },
          ],
          depts: [
            { id: 'd_approved', name: 'Approved Self-Check Dept', type: 'service', fteTrig: .5, approvedForReporting: true,
              volDrivers: [{ name: 'Widgets', perFTE: 100, current: 900 }] }, // 9 implied vs 5 productive — well Flagged
            { id: 'd_pending', name: 'Pending Self-Check Dept', type: 'service', fteTrig: .5,
              volDrivers: [{ name: 'Widgets', perFTE: 100, current: 900 }] }, // identical shape, NOT approved
          ],
          acts: [{ name: 'Core', billable: true }], impls: [], pipeline: [], haircut: { pto:0, hol:0, sick:0, interrupt:0 },
        };
        function buildTeam(deptId, prefix) {
          return [1,2,3,4,5].map(function(i){ return { id: prefix+i, name: 'Staff '+i, dept: deptId, roleId: deptId==='d_approved'?'r_appr':'r_pend', hrs: 40, vacant: false }; });
        }
        S.team = buildTeam('d_approved', 'ea_').concat(buildTeam('d_pending', 'ep_'));
        S.statusHistory = {};

        record('Validation Gate', 'isApprovedForReporting reads the flag correctly (true)', isApprovedForReporting(S.depts[0]) === true);
        record('Validation Gate', 'isApprovedForReporting defaults to false when the field is unset', isApprovedForReporting(S.depts[1]) === false);
        record('Validation Gate', 'isApprovedForReporting is defensive against a missing department', isApprovedForReporting(undefined) === false && isApprovedForReporting(null) === false);

        var sigApproved = computeDeptSignal(S.depts[0]);
        var sigPending = computeDeptSignal(S.depts[1]);
        record('Validation Gate', 'Sanity check — both fixture departments genuinely Flag on their own formula (same shape, same numbers)', sigApproved.flagged === true && sigPending.flagged === true, 'approved.flagged=' + sigApproved.flagged + ' pending.flagged=' + sigPending.flagged);

        var wi = computeWeeklyImpact();
        record('Validation Gate', 'Weekly Impact hrsShort counts ONLY the approved department\'s gap (4 FTE * 40 = 160), not both', wi.hrsShort === 160, 'got ' + wi.hrsShort);
        record('Validation Gate', 'Weekly Impact correctly reports 1 unapproved-but-Flagged department', wi.unapprovedFlaggedCount === 1, 'got ' + wi.unapprovedFlaggedCount);
        record('Validation Gate', 'Weekly Impact correctly reports 1 unapproved department overall', wi.unapprovedCount === 1, 'got ' + wi.unapprovedCount);
      })();

      // ── 18. Team Fragility — Ramp Period actually drives the recovery curve ──
      // A reviewer caught that the recovery curve used a hardcoded 8-week ramp regardless of the
      // editable Ramp Period field, so changing it appeared to do nothing. Permanent regression
      // coverage for the fix: two different Ramp Period values must produce two different curves.
      (function() {
        S = {
          settings: { orgHeadcount: 335, hrBenchmarkRatio: 60 }, managerSignals: {}, deptVolumes: {},
          roles: [
            { id: 'r_ramp1', dept: 'd_ramp', name: 'Analyst', tth: 8, rampWks: 4, pct: [1.0], hrs: 40 },
            { id: 'r_ramp2', dept: 'd_ramp', name: 'Sr Analyst', tth: 8, rampWks: 4, pct: [1.0], hrs: 40 },
          ],
          depts: [{ id: 'd_ramp', name: 'Ramp Self-Check Dept', type: 'service' }],
          acts: [{ name: 'Core', billable: true }], impls: [], pipeline: [], haircut: { pto:0, hol:0, sick:0, interrupt:0 },
        };
        S.team = [
          { id: 'e_ramp_1', name: 'A', dept: 'd_ramp', roleId: 'r_ramp1', hrs: 40, vacant: false, seniority: 'senior', deptProb: 0.9 },
          { id: 'e_ramp_2', name: 'B', dept: 'd_ramp', roleId: 'r_ramp2', hrs: 40, vacant: false, seniority: 'senior', deptProb: 0.9 },
          { id: 'e_ramp_3', name: 'C', dept: 'd_ramp', roleId: 'r_ramp1', hrs: 40, vacant: false, seniority: 'mid', deptProb: 0.05 },
        ];
        var fragShort = calcFragility('d_ramp');
        var pctAt20Short = (fragShort.curve.find(function(c){ return c.wk === 20; }) || {}).pct;

        S.roles.forEach(function(r){ r.rampWks = 20; });
        var fragLong = calcFragility('d_ramp');
        var pctAt20Long = (fragLong.curve.find(function(c){ return c.wk === 20; }) || {}).pct;

        record('Team Fragility', 'Ramp Period genuinely changes the recovery curve (a 4-week vs 20-week ramp differ at week 20)', pctAt20Short !== pctAt20Long, 'short=' + pctAt20Short + ' long=' + pctAt20Long);
        record('Team Fragility', 'A shorter Ramp Period recovers capacity at least as fast as a longer one', pctAt20Short >= pctAt20Long, 'short=' + pctAt20Short + ' long=' + pctAt20Long);
      })();

      // ── 19. Demand Tiers — deletion protection + collision-safe key generation ─
      // A reviewer caught that T1/T2/T3 could be deleted, and that addTier()'s old
      // 'T'+(length+1) logic could then regenerate a key that already existed — e.g. delete T2
      // (length becomes 2), add a tier, get 'T3', which collides with the real T3. Business
      // Case's revenue-at-risk math depends on T1/T2 existing and being unique, so a collision
      // silently corrupts that calculation with no visible error.
      (function() {
        if (!S.settings) S.settings = {};
        var savedTiers = S.settings.tiers ? JSON.parse(JSON.stringify(S.settings.tiers)) : null;
        S.settings.tiers = [{key:'T1',label:'Core',priority:'Critical'},{key:'T2',label:'Program',priority:'High'},{key:'T3',label:'Ad Hoc',priority:'Standard'}];
        S.settings.tiers.splice(1, 1); // remove T2 directly (bypassing any UI-level block) to test addTier()'s own safety net independently
        addTier();
        var keys = S.settings.tiers.map(function(t){ return t.key; });
        var dupes = keys.filter(function(k,i){ return keys.indexOf(k) !== i; });
        record('Demand Tiers', 'addTier() never produces a duplicate key, even after a mid-list deletion (the exact scenario a reviewer found)', dupes.length === 0, 'keys=' + JSON.stringify(keys));
        record('Demand Tiers', 'addTier() fills the lowest freed number rather than always incrementing length+1', keys.indexOf('T2') !== -1, 'keys=' + JSON.stringify(keys));
        if (savedTiers) S.settings.tiers = savedTiers;
      })();

    } catch (e) {
      record('Self-check runtime error', 'The check itself threw an exception — this means a function signature or field name it depends on has changed', false, String(e && e.stack || e));
    } finally {
      S = _savedS;
    }

    renderModelSelfCheckResults(results);
    return results;
  }

  // Stubbed by scripts/extract-engine.mjs: presentation only, no calculation.
  function renderModelSelfCheckResults() {}
  return {
    computeDeptSignal,
    applyManagerEscalation,
    computeDeptSeverity,
    productiveFTEForTeam,
    productiveFTEForTeamRaw,
    fteWeightedCount,
    empRampMult,
    timeOffAvailabilityMult,
    haircutMult,
    seniorityMult,
    resolveVolumeDriverValue,
    validateVolPeriodHistory,
    checkConsecutivePeriods,
    getTradeCov,
    TRADE_COV_ROWS,
    TRADE_COV_BLOCKS,
    TRADE_COV_DEFAULT,
    COVERAGE_DEPT_ROW,
    COVERAGE_DEPT_IDS,
    COVERAGE_VOL_REF,
    isHireEligible,
    isVacancyRecruitable,
    isActiveRosterRecord,
    computeBizCaseVerdict,
    computeWeeklyImpact,
    isApprovedForReporting,
    getPlanAllocations,
    syncAllVacancyRecords,
    syncVacancyRecordsForRoleDept,
    calcFragility,
    empBillCapAdjusted,
    mentorDragForDept,
    planBuiltImportMerge,
    commitBuiltImportMerge,
    matchDept,
    matchRole,
    BUILT_IMP_FIELDS,
    BUILT_IMP_PATTERNS,
    BUILT_IMP,
    computeRevenueAtRisk,
    outageMultForEmp,
    runModelSelfCheck,
    allOrgSkills,
    deptSkills,
    skillCoverage,
    checkSkillCoverage,
  };
}
